import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Brain, Bot, Boxes, Coins, Cpu, Eye, EyeOff, RefreshCw, Settings, Shield,
  ChevronDown, ChevronRight, Loader, AlertTriangle, User, Clock, Github, Cloud,
  ExternalLink, X, History, ShieldCheck, Lock, Download,
} from "lucide-react";
import Navigation from "../components/Navigation";
import Card from "../components/Card";
import Button from "../components/Buttons";
import AiIntelligenceCharts, { EstateGrowthChart } from "../components/AiIntelligenceCharts";
import SpendTrajectoryChart from "../components/SpendTrajectoryChart";
import AiActionCenter from "../components/AiActionCenter";
import AiEstateChangePanel from "../components/AiEstateChangePanel";
import AiUsageCostPanel from "../components/AiUsageCostPanel";
import AiPosturePanel from "../components/AiPosturePanel";
import AiFeedOperations from "../components/AiFeedOperations";
import AiAssetBusinessAnchor from "../components/AiAssetBusinessAnchor";
import AiBehavior360Panel from "../components/AiBehavior360Panel";
import AiPlaybookPanel from "../components/AiPlaybookPanel";
import AiAssetComments from "../components/AiAssetComments";
import AssetActivityTrail, { useAssetHistory, historyTabCount } from "../components/AssetActivityTrail";
import AiEntryPathPanel, { useEntryPaths } from "../components/AiEntryPathPanel";
import AiIntelligenceGraph, {
  edgeKey, filterGraph, FILTERABLE_KINDS, KIND_LABEL,
} from "../components/AiIntelligenceGraph";
import AiDataLeakFindings from "../components/AiDataLeakFindings";
import IntelligenceNarrative from "../components/IntelligenceNarrative";
import AiResponseActionsPanel from "../components/AiResponseActionsPanel";
import AiRespondTab from "../components/AiRespondTab";
import AiSuppressRiskModal from "../components/AiSuppressRiskModal";
import AiTiersTab from "../components/AiTiersTab";
import AiFleetPortfolioPanel from "../components/AiFleetPortfolioPanel";
import AiDataLeaksPanel from "../components/AiDataLeaksPanel";
import AiScanFrequencySelect from "../components/AiScanFrequencySelect";
import AiAskDrawer, { AskButton } from "../components/AiAskDrawer";
import { useApi } from "../hooks/useApi";
import { useRBAC } from "../hooks/useRBAC";
import { useAuth } from "../contexts/AuthContext";
import { useShowAiIntelligence, useAiIntelPreview } from "../contexts/FeatureFlagsContext";
import { resolveTabFromSearch, tabUrl, visibleTab } from "../utils/aiIntelNav";
import { assetNotReReadSince, signalNotReReadSince } from "../utils/aiSignalFreshness";
import { ownerProvenance, agentCoverage, AGENT_COVERAGE_LABEL } from "../utils/aiOpenAiAgentAttribution";
import { signalSourceLabel } from "../utils/signalSourceLabel";
import { figureOrReason, asSentence, isWithheld } from "../utils/aiFleetPortfolio";

const KIND_META = {
  Agent:           { label: "Agent",            Icon: Bot },
  Application:     { label: "AI Application",   Icon: Boxes },
  ModelDeployment: { label: "Model Deployment", Icon: Cpu },
  McpServer:       { label: "MCP Server",       Icon: Boxes },
  Workload:        { label: "Workload",         Icon: Cloud },
  // A person's own AI usage at a provider, keyed on the provider's user id.
  PersonalUse:     { label: "Personal use",     Icon: User },
};

const SOURCE_BADGE = {
  Entra:  "bg-blue-50 text-blue-700 border border-blue-200",
  Azure:  "bg-indigo-50 text-indigo-700 border border-indigo-200",
  AWS:    "bg-amber-50 text-amber-700 border border-amber-200",
  "GCP":  "bg-emerald-50 text-emerald-700 border border-emerald-200",
  OpenAI: "bg-teal-50 text-teal-700 border border-teal-200",
  Anthropic: "bg-rose-50 text-rose-700 border border-rose-200",
  GitHub: "bg-gray-100 text-gray-700 border border-gray-300",
  GitLab: "bg-orange-50 text-orange-700 border border-orange-200",
  AzureDevOps: "bg-sky-50 text-sky-700 border border-sky-200",
  "Copilot Studio": "bg-violet-50 text-violet-700 border border-violet-200",
};

// Badge/provider text for sources whose raw value is not what the UI should say. Unlisted
// sources render their raw value, so only these change.
const SOURCE_DISPLAY = { GCP: "Google Cloud" };

// Rule codes are the risks' RiskCategory values; the UI says them in English.
const RULE_LABEL = {
  OwnerlessAiAgent: "AI agent without an accountable owner",
  OverPrivilegedAiIdentity: "Over-privileged AI identity",
  ShadowAiAsset: "Shadow AI asset",
  AiAssetExposedSecret: "AI asset with an exposed secret",
  StaleAiProviderCredential: "Stale AI provider credential",
  AiAgentDataExfiltrationPath: "Data exfiltration path",
  AiAgentExternalDataFlow: "External data flow",
  AiAgentUnaccountablePrivilegedTool: "Unaccountable privileged tool",
  AiAgentUntrustedContentExecution: "Untrusted content execution",
  AiAgentReachedUnpermittedResource: "Reached a resource outside its permissions",
};

/**
 * Agency is shown only when it says more than the kind already does. An asset whose kind is Agent
 * and whose agenticity is Confirmed adds nothing by repeating itself; an Application that is a
 * probable agent, or an agent observed acting unattended, is worth a word.
 *
 * Two fields rather than one: whether it acts, and how independently. "Confirmed" and "Assistive"
 * are both true of the same Copilot agent, so a single label could only ever tell half of it.
 */
const AGENTICITY_LABEL = {
  Probable: "probable agent",
};

const AUTONOMY_LABEL = {
  Assistive: "human-driven",
  Supervised: "supervised",
  Autonomous: "autonomous",
};

/** Kind first, then whatever agency adds that the kind did not already say. */
// §11k: per-currency amounts, never merged — "$142" or "$142 + 96 EUR".
const formatSpend = (amounts) => amounts
  .map((a) => (a.currency === "USD"
    ? `$${Math.round(a.amount).toLocaleString()}`
    : `${Math.round(a.amount).toLocaleString()} ${a.currency}`))
  .join(" + ");

const describeAsset = (kindLabel, asset) => [
  kindLabel,
  AGENTICITY_LABEL[asset.agenticity],
  AUTONOMY_LABEL[asset.autonomyMode],
].filter(Boolean).join(" · ");

const ACTIVITY_LABEL = {
  ResponseRequested: "Response requested",
  ResponseApproved: "Response approved",
  ResponseRejected: "Response rejected",
  ResponseCancelled: "Request cancelled",
  ResponseExecuted: "Containment executed",
  ResponseOverridden: "Containment executed without approval",
  ResponseExecuteFailed: "Response failed",
  ResponseVerified: "Provider confirmed containment",
  ResponseVerifyFailed: "Verification pending",
  ResponseReversed: "Containment reversed",
  ContainmentReverted: "Containment reverted",
  ContainmentObservedAfterWindow: "Scan found containment still in place after its window",
  RiskSuppressed: "Marked not a risk",
  RiskSuppressionWithdrawn: "Not-a-risk withdrawn",
  RiskSuppressionLapsed: "Not-a-risk lapsed",
};

// Discovery-confidence grades below Confirmed get a visible chip. Confidence no longer decides
// how an asset is labelled — that inferred agency from evidence strength, which are different
// questions; AgencyLevel answers the second one directly (§8.5).
const CONFIDENCE_CHIP = {
  Corroborated: "bg-green-50 text-green-700 border border-green-200",
  Probable:     "bg-yellow-50 text-yellow-700 border border-yellow-200",
  Suspected:    "bg-orange-50 text-orange-700 border border-orange-200",
  Declared:     "bg-gray-100 text-gray-600 border border-gray-300",
  Unverified:   "bg-gray-100 text-gray-600 border border-gray-300",
};

const RATING_PILL = {
  CRITICAL: "bg-red-100 text-red-700 border border-red-200",
  HIGH:     "bg-orange-50 text-orange-700 border border-orange-200",
  MEDIUM:   "bg-yellow-50 text-yellow-700 border border-yellow-200",
  LOW:      "bg-green-50 text-green-700 border border-green-200",
};

// Trailing "+" — the same lower-bound convention the assessment views use for FAIR figures: an
// exposure figure only covers the risks that carry a monetary estimate, so it is a floor, not a total.
function formatImpact(val) {
  if (val == null) return "—";
  const n = Number(val);
  if (n <= 0) return "$0";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M+`;
  if (n >= 1_000)     return `$${(n / 1_000).toFixed(1)}K+`;
  return `$${Math.round(n).toLocaleString()}+`;
}

/**
 * Compact money for the summary card, from the usage ledger's 30-day read. The ledger's rules
 * survive the compression: no cost reported is "Not reported" rather than $0, and a mixed-currency
 * estate renders each currency as itself — adding them would produce a number that is not money.
 */
function formatCost30(usage) {
  if (!usage?.hasData) return "—";
  const fmt = (v, currency) => {
    if (v == null) return "Not reported";
    return new Intl.NumberFormat(undefined, {
      style: "currency", currency, maximumFractionDigits: 2,
    }).format(v);
  };
  if (usage.hasMixedCurrencies) {
    return (usage.costByCurrency || []).map((c) => fmt(c.costUsd, c.currency)).join(" + ");
  }
  return fmt(usage.providerReportedCostUsd, usage.currency || "USD");
}

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const secs = Math.floor((Date.now() - new Date(dateStr)) / 1000);
  if (secs < 60)    return "just now";
  if (secs < 3600)  return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  const days = Math.floor(secs / 86400);
  if (days === 1)   return "yesterday";
  if (days < 30)    return `${days} days ago`;
  return `${Math.floor(days / 30)} months ago`;
}

/** Shown instead of "Last seen" when the evidence was carried forward, not re-read. */
function notReReadLabel(lastConfirmedAt) {
  return lastConfirmedAt
    ? `Not re-read — last confirmed ${timeAgo(lastConfirmedAt)}`
    : "Not re-read — last confirmed time unknown";
}

const REPORT_ERROR = "The report could not be generated. Try again.";

/**
 * Downloads the AI Intelligence executive report PDF. Whether the report is refused for this caller
 * is known only from the response, so a withheld answer swaps the button for the server's sentence.
 * Nothing is requested until the button is pressed.
 */
export function AiExecutiveReportAction({ orgId }) {
  const apiCall = useApi();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [withheldReason, setWithheldReason] = useState(null);
  const inFlight = useRef(false);
  const currentOrg = useRef(orgId);

  // A refusal belongs to the organization it was given for.
  useEffect(() => {
    currentOrg.current = orgId;
    setWithheldReason(null);
    setFailed(false);
  }, [orgId]);

  const download = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    const requestedFor = orgId;
    setBusy(true);
    setFailed(false);
    try {
      const response = await apiCall("GET", "/ai-assets/executive-report-pdf");
      // The answer is for an organization no longer on screen.
      if (currentOrg.current !== requestedFor) return;
      // useApi resolves undefined when it has redirected to sign in.
      if (response === undefined) return;
      const reason = isWithheld(response) && typeof response.reason === "string" ? response.reason.trim() : "";
      if (reason) {
        setWithheldReason(reason);
      } else if (!isWithheld(response) && response?.pdf_data && response?.filename) {
        const link = document.createElement("a");
        link.href = `data:application/pdf;base64,${response.pdf_data}`;
        link.download = response.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setFailed(true);
      }
    } catch {
      if (currentOrg.current === requestedFor) setFailed(true);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  if (withheldReason) {
    return <p className="text-xs text-content-secondary max-w-xs">{withheldReason}</p>;
  }
  return (
    <>
      {failed && <p role="alert" className="text-xs text-red-600 max-w-xs">{REPORT_ERROR}</p>}
      <Button onClick={download} disabled={busy}>
        {busy
          ? (<><Loader className="w-4 h-4 mr-1 animate-spin" /> Executive report</>)
          : (<><Download className="w-4 h-4 mr-1" /> Executive report</>)}
      </Button>
    </>
  );
}

export default function AiIntelligenceDashboard() {
  const apiCall = useApi();
  const navigate = useNavigate();
  const showAiIntelligence = useShowAiIntelligence();
  // Features:AiIntelPreview: hides the Data leaks tab, the inventory leak chip and the asset's leak block.
  const aiIntelPreview = useAiIntelPreview();
  const { canManageOrganizationRisks, canAdministerOrganization, canRunAgent, canViewAllAiEstateData } = useRBAC();
  // Roles arrive after the first render. Only an explicit `initialized === false` means they are
  // not here yet; anything else (including a context without the field) is treated as ready.
  const { initialized } = useAuth();
  const rolesReady = initialized !== false;

  // Feature-flagged (Features:ShowAiIntelligence — off in Production). The nav entry is hidden
  // too; this covers direct URL access.
  useEffect(() => {
    if (!showAiIntelligence) navigate("/dashboard", { replace: true });
  }, [showAiIntelligence, navigate]);
  const org = JSON.parse(localStorage.getItem("selectedOrganization") || "{}");
  const orgId = org?.organizationId;
  const canManage = canManageOrganizationRisks(orgId);
  // The org-wide AI aggregates (summary, assets, spend, changes, usage, graph, action lists) are
  // served only to callers with the estate capability; everyone else gets a 403 for each. So the
  // gate is on what is REQUESTED and MOUNTED, not just on which tabs are shown.
  const canViewAll = canViewAllAiEstateData(orgId);
  // §11i: the two response-policy toggles loosen the rails that constrain risk managers, so
  // only ADMIN+ may change them (the server enforces this field-level too).
  const canAdminister = canAdministerOrganization(orgId);

  // The tab lives in the URL (?tab=) so links land where they point: My Tasks deep-links open
  // Inventory, a shared "usage" link opens Usage & Cost. Settings stays off the URL — it is a
  // mode entered through the gear, not a place to link to.
  const [tab, setTab] = useState(() => resolveTabFromSearch(window.location.search));
  // Portfolio (org-wide aggregates) and Data leaks (names from every leak finding) are org-wide
  // reads (server: CanViewAllOrganizationData; canRunAgent is the same VIEWER+@ORG rule). Derived
  // at render time, not in the one-shot initialiser above, because roles can be empty on first
  // render: a ?tab=portfolio or ?tab=leaks link for a caller without that access renders Overview
  // instead of a tab that would 403.
  // Settings is for callers who can manage organization risks (AX-268): the gear is hidden from
  // everyone else, and a Settings state reached without that permission (roles changing under an
  // open tab) renders Overview.
  const viewTab = tab === "settings" && !canManage
    ? "overview"
    : visibleTab(tab, canRunAgent(orgId), aiIntelPreview, canViewAll);
  const switchTab = (next) => {
    setTab(next);
    if (next === "settings") return;
    window.history.replaceState({}, "",
      tabUrl(window.location.pathname, window.location.search, next));
  };
  const [summary, setSummary] = useState(null);
  // Inventory name search — client-side; the list is already loaded and filtered server-side by
  // kind/governance/status.
  const [search, setSearch] = useState("");
  // { total, decisionsWaiting } from the always-mounted Action Center — the Needs-attention
  // tab's badge, kept live whichever tab is showing.
  const [attention, setAttention] = useState({ total: 0, decisionsWaiting: 0 });
  // The alert type and window chosen in the estate change panel, carried to the Action Center's list.
  const [changeFilter, setChangeFilter] = useState(null);
  // Row whose tier pulldown is mid-save. Saved in place — the row is patched locally rather
  // than reloading the page, so the pulldown disables, saves, and re-enables and nothing jumps.
  const [savingTierId, setSavingTierId] = useState(null);
  const [spendByAsset, setSpendByAsset] = useState({});
  const [spendUnavailable, setSpendUnavailable] = useState(false);
  // The Tiers tab's copy of the estate: ALWAYS unfiltered (active only) — a tab whose claim is
  // "the whole estate, grouped" must not inherit the Inventory tab's kind/governance filters.
  const [allAssets, setAllAssets] = useState([]);
  // The last 30 days of the usage ledger, for the summary card only. Fetched once — the ledger
  // moves daily, so neither the filter changes nor the minute poll need to re-read it.
  const [usage30, setUsage30] = useState(null);
  const [assets, setAssets] = useState([]);
  const [config, setConfig] = useState(null);
  // Last server state of the config, so the settings tab can tell edited from saved and offer to
  // discard. Kept as a separate state (not a ref) because the dirty flag drives rendering.
  const [savedConfig, setSavedConfig] = useState(null);
  const [connections, setConnections] = useState([]);
  const [awsConnectors, setAwsConnectors] = useState([]);
  const [grcConnectors, setGrcConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  // Whether this org's AI estate is constructed reference data, from the config read's derived
  // constructedEstate flag: "pending" until the first config read settles, "failed" if it never
  // succeeded, otherwise "constructed" | "observed". Unknown is unsafe — the module renders only
  // once the answer is known, so no panel can show a constructed estate without its banner.
  const [estateNature, setEstateNature] = useState("pending");
  // Which discovery sources leave the Overview counts stale, from GET /ai-assets/coverage.
  // read: "pending" | "ok" | "failed". Same rule as estateNature: a failed re-read keeps the last
  // known answer; only a never-established one is "failed", and "failed" is said on screen.
  // refreshFailed: the last re-read failed after an answer was established. The prior rows stay
  // rendered, but the counts beside them refreshed, so the note also says it cannot confirm them
  // — otherwise a prior all-current (silent) answer would vouch for figures nobody re-checked.
  // Cleared by the next successful coverage read.
  const [discoveryCoverage, setDiscoveryCoverage] = useState({ read: "pending", data: null, refreshFailed: false });

  const [filterKind, setFilterKind] = useState("");
  const [filterGovernance, setFilterGovernance] = useState("");
  const [showStale, setShowStale] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  // The "Ask" drawer (the AI agent scoped to this module). Closed whenever Settings opens: the
  // drawer answers about the estate, and Settings is a mode, not a view of it.
  const [askOpen, setAskOpen] = useState(false);
  useEffect(() => { if (viewTab === "settings") setAskOpen(false); }, [viewTab]);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const configDirty = !!config && !!savedConfig && JSON.stringify(config) !== JSON.stringify(savedConfig);
  // loadData is memoised on the filters only, so it would close over a stale configDirty.
  const dirtyRef = useRef(configDirty);
  useEffect(() => { dirtyRef.current = configDirty; }, [configDirty]);

  // `silent` skips the loading state: a background refresh must not blank the table the user is
  // reading and replace it with a spinner every minute.
  const loadData = useCallback(async ({ silent = false } = {}) => {
    // No org-wide request at all without the capability (including the empty-roles first render,
    // which refetches once roles arrive and this callback changes).
    if (!canViewAll) return;
    if (!silent) setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterKind) params.append("kind", filterKind);
      if (filterGovernance) params.append("governanceState", filterGovernance);
      if (!showStale) params.append("status", "Active"); // current estate by default; stale is opt-in
      const [sum, ast, allAst, cfg, conns, grcConns, spend, cov] = await Promise.all([
        apiCall("GET", "/ai-assets/summary").catch(() => null),
        apiCall("GET", `/ai-assets/assets?${params.toString()}`).catch(() => []),
        apiCall("GET", "/ai-assets/assets?status=Active").catch(() => []),
        apiCall("GET", "/ai-assets/config").catch(() => null),
        apiCall("GET", "/source-code-connections").catch(() => []),
        apiCall("GET", "/grc-connectors").catch(() => []),
        apiCall("GET", "/ai-assets/spend-by-asset?days=30").catch(() => null),
        apiCall("GET", "/ai-assets/coverage").catch(() => null),
      ]);
      setSummary(sum);
      setAssets(Array.isArray(ast) ? ast : []);
      setAllAssets(Array.isArray(allAst) ? allAst : []);
      // §11k: map keyed by asset id. Absence means "no billed spend attributed", which for an
      // agent usually means billing lands on its provider container — a different statement
      // from $0, so absent rows render nothing rather than a zero. A FAILED read is a third
      // statement again (review finding): the chips disappear AND the page says why.
      setSpendUnavailable(!Array.isArray(spend));
      setSpendByAsset(Object.fromEntries(
        (Array.isArray(spend) ? spend : []).map((s) => [s.aiAssetId, s.amounts || []])));
      setSavedConfig(cfg);
      // A failed re-read keeps the last known answer; only a never-established one is "failed".
      setEstateNature((prev) => (cfg
        ? (cfg.constructedEstate === true ? "constructed" : "observed")
        : (prev === "constructed" || prev === "observed" ? prev : "failed")));
      setDiscoveryCoverage((prev) => (cov && Array.isArray(cov.sources)
        ? { read: "ok", data: cov, refreshFailed: false }
        : (prev.read === "ok"
          ? { ...prev, refreshFailed: true }
          : { read: "failed", data: null, refreshFailed: false })));
      // A refetch triggered while settings are open (Scan Now, a filter change) must not silently
      // throw away edits the user has not saved yet — keep them and only take the server copy when
      // there is nothing to lose.
      setConfig((prev) => (prev && dirtyRef.current ? prev : cfg));
      setConnections(Array.isArray(conns) ? conns : []);
      const grcList = Array.isArray(grcConns) ? grcConns : [];
      setGrcConnectors(grcList);
      setAwsConnectors(grcList.filter((c) => c.sourceSystem === "AWS"));
    } finally {
      if (!silent) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKind, filterGovernance, showStale, canViewAll]);

  useEffect(() => { loadData(); }, [loadData]);

  // A caller without the estate capability requests exactly one org-wide-shaped route: coverage,
  // which task 01 left open so they can judge whether the assets they can open are current. Every
  // other org-wide route stays unrequested. A failure only shows the "cannot confirm" note.
  useEffect(() => {
    if (canViewAll || !rolesReady) return undefined;
    let cancelled = false;
    (async () => {
      let cov = null;
      try { cov = await apiCall("GET", "/ai-assets/coverage"); } catch (e) { cov = null; }
      if (cancelled) return;
      setDiscoveryCoverage(cov && Array.isArray(cov.sources)
        ? { read: "ok", data: cov, refreshFailed: false }
        : { read: "failed", data: null, refreshFailed: false });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewAll, rolesReady]);

  useEffect(() => {
    if (!canViewAll) return;
    apiCall("GET", "/ai-assets/usage?days=30")
      .then(setUsage30)
      .catch(() => setUsage30(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewAll]);

  // Background refresh, so a scan finishing (or the nightly worker running) shows up without a
  // manual reload. Silent, so the table never flashes a spinner at someone reading it.
  //
  // Paused on the settings tab (nothing there changes server-side, and unsaved edits are already
  // guarded by dirtyRef — no reason to poll at all), while a scan is in flight (that has its own
  // completion poll), and while the tab is hidden: a backgrounded tab polling every minute is
  // load nobody is looking at.
  useEffect(() => {
    if (!canViewAll || viewTab === "settings" || scanning || savingConfig) return;
    const id = setInterval(() => {
      if (!document.hidden) loadData({ silent: true });
    }, 60000);
    return () => clearInterval(id);
  }, [viewTab, scanning, savingConfig, loadData, canViewAll]);

  // Tier saves patch the one row in place — no loadData(), no scroll jump, no pane collapse.
  // The estate-change alert the server raises shows up on the next natural refresh.
  const saveTier = async (asset, value) => {
    const tier = value === "" ? null : parseInt(value, 10);
    setSavingTierId(asset.id);
    try {
      await apiCall("PUT", `/ai-assets/assets/${asset.id}/tier`, { tier });
      setAssets((prev) => prev.map((a) => (a.id === asset.id ? { ...a, tier } : a)));
      setAllAssets((prev) => prev.map((a) => (a.id === asset.id ? { ...a, tier } : a)));
    } catch (e) {
      alert(e?.response?.data?.message || "Could not save the tier.");
    } finally {
      setSavingTierId(null);
    }
  };

  const toggleExpand = async (asset) => {
    if (expandedId === asset.id) {
      setExpandedId(null);
      setDetail(null);
      return;
    }
    setExpandedId(asset.id);
    setDetail(null);
    setDetailLoading(true);
    try {
      // The graph is fetched by AssetRelationships itself — it owns the includeGone toggle, and a
      // graph failure there never costs the user their evidence pane.
      const d = await apiCall("GET", `/ai-assets/assets/${asset.id}`);
      setDetail(d);
    } catch {
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  // Re-read the open asset in place — used after an edit that changes the asset itself (declaring
  // an owner flips governance), without collapsing the pane the user is looking at.
  const refreshDetail = async (assetIdToRefresh) => {
    try {
      const d = await apiCall("GET", `/ai-assets/assets/${assetIdToRefresh}`);
      setDetail(d);
    } catch {
      /* leave the current detail in place — a failed refresh should not blank the pane */
    }
    // Silent: the row's governance/owner columns changed too, but an in-place edit must not
    // swap the whole page to its loading state — that read as a flash after every save.
    loadData({ silent: true });
  };

  // From the exposure chart (now on Overview): switch to Inventory, make sure the asset's row
  // is visible (clear filters, opt into stale if needed), then expand its detail pane.
  const openAssetFromChart = (assetId, isStale) => {
    switchTab("inventory");
    setFilterKind("");
    setFilterGovernance("");
    setSearch("");
    if (isStale) setShowStale(true);
    if (expandedId !== assetId) toggleExpand({ id: assetId });
  };

  // Deep link (?assetId=N) from My Tasks response-approval entries: open the asset's detail pane
  // once, then strip the param so refreshes and tab switches don't keep re-expanding it. Same
  // visibility handling as the graph path — the target's staleness is unknown, so opt in.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const linkedAssetId = parseInt(params.get("assetId"));
    if (!linkedAssetId) return;
    window.history.replaceState({}, "", window.location.pathname);
    // The link points at an asset, so land on the list that shows it — Overview being the
    // default must not swallow deep links.
    setTab("inventory");
    setShowStale(true);
    toggleExpand({ id: linkedAssetId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // From a graph node that mirrors another asset. Unlike the chart path we don't know whether the
  // target is stale, and a stale asset is hidden by default — so opt into stale rather than
  // expanding a row that will not be rendered.
  const openAssetFromGraph = (assetId) => {
    // Reached from the estate tab too, where the inventory list is not on screen — without this
    // the row expands behind the graph and the click looks dead.
    switchTab("inventory");
    setFilterKind("");
    setFilterGovernance("");
    setSearch("");
    setShowStale(true);
    if (expandedId !== assetId) toggleExpand({ id: assetId });
  };

  // A scan runs seven discovery sources, so it finishes whenever it finishes. Poll lastScannedAt
  // as the completion signal rather than guessing a duration — the old fixed 15s wait refreshed
  // into a half-finished scan and left the page stale until a manual reload.
  const triggerScan = async () => {
    const preScanTime = config?.lastScannedAt ?? null;
    setScanning(true);
    try {
      await apiCall("POST", "/ai-assets/scan");
      const started = Date.now();
      const poll = setInterval(async () => {
        try {
          const latest = await apiCall("GET", "/ai-assets/config");
          const done = latest?.lastScannedAt && latest.lastScannedAt !== preScanTime;
          const timedOut = Date.now() - started > 240000;
          if (done || timedOut) {
            clearInterval(poll);
            setScanning(false);
            loadData();
          } else {
            // Show results as they land rather than making the whole page wait on the timestamp.
            // lastScannedAt is written after risk generation, which runs an LLM narrative and the
            // financial engine per new risk — but correlation, the graph and the alerts finished
            // long before that, so their output is already queryable.
            loadData({ silent: true });
          }
        } catch { /* keep polling — a transient failure is not a finished scan */ }
      }, 6000);
    } catch {
      setScanning(false);
    }
  };

  const saveConfig = async () => {
    if (!config) return;
    setSavingConfig(true);
    try {
      const saved = await apiCall("POST", "/ai-assets/config", config);
      setConfig(saved);
      setSavedConfig(saved);
      // The save response carries the same derived flag, so the banner survives a settings save.
      setEstateNature(saved?.constructedEstate === true ? "constructed" : "observed");
      switchTab("overview"); // a successful save closes settings — the work is done
    } finally {
      setSavingConfig(false);
    }
  };

  // §11i: the two response-policy toggles commit immediately through their own ADMIN-only
  // endpoint — they never ride along on (or get lost by) the whole-config save. Expected values
  // are the last persisted state, so a stale tab gets a 409 carrying the current policy instead
  // of silently overwriting a newer administrator's decision.
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [policyNotice, setPolicyNotice] = useState(null);
  const savePolicy = async (patch) => {
    if (!savedConfig) return;
    setSavingPolicy(true);
    setPolicyNotice(null);
    const applyBoth = (values) => {
      setConfig((c) => (c ? { ...c, ...values } : c));
      setSavedConfig((c) => (c ? { ...c, ...values } : c));
    };
    try {
      const saved = await apiCall("PUT", "/ai-assets/config/response-policy", {
        tier1ImmediateAllowed: savedConfig.tier1ImmediateAllowed === true,
        irreversibleImmediateAllowed: savedConfig.irreversibleImmediateAllowed === true,
        ...patch,
        expectedTier1ImmediateAllowed: savedConfig.tier1ImmediateAllowed === true,
        expectedIrreversibleImmediateAllowed: savedConfig.irreversibleImmediateAllowed === true,
      });
      applyBoth(saved);
    } catch (e) {
      const body = e?.response?.data;
      if (e?.response?.status === 409 && body) {
        // A newer administrator decided first — adopt what is actually stored and say so.
        applyBoth({
          tier1ImmediateAllowed: body.tier1ImmediateAllowed === true,
          irreversibleImmediateAllowed: body.irreversibleImmediateAllowed === true,
        });
        setPolicyNotice(body.message
          || "The response approval policy changed since this page loaded — showing the current policy.");
      } else {
        setPolicyNotice("The policy change could not be saved. Reload the page and try again.");
      }
    } finally {
      setSavingPolicy(false);
    }
  };

  // Leaving settings without saving: confirm, then put the edits back to the last saved state so
  // reopening the tab doesn't show changes that were never persisted.
  const closeSettings = () => {
    if (configDirty) {
      if (!window.confirm("You have unsaved discovery settings. Close without saving?")) return;
      setConfig(savedConfig);
    }
    switchTab("overview");
  };

  // Four headline metrics, decision-oriented. Shadow/ownerless/over-privileged moved into the
  // governance chart and posture panel — they were cards restating what the charts show.
  // Critical/high open risks is derived from the per-severity remediation split the summary
  // already carries, so no extra backend read.
  const critHighOpenRisks = (summary?.remediationProgress?.bySeverity || [])
    .filter((row) => row.severity === "CRITICAL" || row.severity === "HIGH")
    .reduce((sum, row) => sum + row.completed + row.inProgress + row.notStarted, 0);
  // Exposure is FAIR expected loss only. When nothing is priced the server sends null with a reason,
  // and the card shows that sentence: a "$0" would read as "no exposure" when the truth is
  // "nothing is priced".
  const exposureFigure = summary
    ? figureOrReason(summary.estimatedExposure, summary.estimatedExposureUnavailableReason, formatImpact)
    : { kind: "value", text: "—" };
  const summaryCards = [
    { label: "Known AI assets", value: summary?.totalAssets ?? "—", Icon: Brain, tone: "text-purple-700" },
    { label: "Shadow Assets", value: summary?.shadowAssets ?? "—", Icon: EyeOff, tone: "text-amber-600" },
    { label: "Critical / High Risks", value: summary ? critHighOpenRisks : "—", Icon: Shield, tone: "text-red-600" },
    {
      label: "Estimated Exposure",
      value: exposureFigure.kind === "value" ? exposureFigure.text : null,
      reason: exposureFigure.kind === "reason" ? exposureFigure.text : null,
      Icon: AlertTriangle,
      tone: "text-red-700",
    },
    { label: "Cost Last 30 Days", value: formatCost30(usage30), Icon: Coins, tone: "text-emerald-600" },
  ];

  // A Data leaks row can link an asset that was later Removed, and the inventory list never
  // carries Removed rows (not even with stale shown). Once that asset's detail is read, show it
  // as a row of its own, so the click lands on the asset instead of on nothing. Assets the list
  // does carry are untouched.
  const pinnedRemovedAsset = detail?.asset && detail.asset.id === expandedId
    && detail.asset.status === "Removed" && !assets.some((a) => a.id === expandedId)
    ? detail.asset
    : null;
  const listedAssets = search.trim()
    ? assets.filter((a) => a.name?.toLowerCase().includes(search.trim().toLowerCase()))
    : assets;
  const visibleAssets = pinnedRemovedAsset ? [pinnedRemovedAsset, ...listedAssets] : listedAssets;

  return (
    <div className="min-h-screen bg-theme-primary">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-content-primary flex items-center gap-2">
              <Brain className="w-7 h-7 text-purple-700" /> AI Intelligence
            </h1>
            <p className="text-sm text-content-secondary mt-1">
              Agentless discovery and risk intelligence for AI agents, applications, and model deployments
            </p>
          </div>
          <div className="flex items-center gap-2">
            {viewTab !== "settings" && (
              <AskButton orgId={orgId} active={askOpen} onClick={() => setAskOpen((v) => !v)} />
            )}
            {canViewAll && <AiExecutiveReportAction orgId={orgId} />}
            {canManage && (
              <Button onClick={triggerScan} disabled={scanning}>
                {scanning
                  ? (<><Loader className="w-4 h-4 mr-1 animate-spin" /> Scanning…</>)
                  : (<><RefreshCw className="w-4 h-4 mr-1" /> Scan Now</>)}
              </Button>
            )}
            {canManage && (
              <button
                onClick={() => (viewTab === "settings" ? closeSettings() : setTab("settings"))}
                className={`p-2 rounded-md border border-border hover:bg-theme-hover ${viewTab === "settings" ? "bg-theme-hover" : ""}`}
                title="Discovery settings"
              >
                <Settings className="w-5 h-5 text-content-secondary" />
              </button>
            )}
          </div>
        </div>
        {!canViewAll ? (
          !rolesReady ? (
            <Card className="overflow-hidden">
              <div className="flex items-center justify-center py-16 text-content-secondary">
                <Loader className="w-5 h-5 mr-2 animate-spin" /> Loading AI asset inventory…
              </div>
            </Card>
          ) : (
            <>
              {/* Not a 404 (that is the module being off) and not an empty dashboard: the caller is
                  told why the organization-wide figures are not theirs. */}
              <div data-testid="org-access-required">
                <Card className="p-6 mb-4">
                  <div className="flex items-start gap-2 text-sm text-content-secondary">
                    <Lock className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>
                      <div className="font-semibold text-content-primary">Organization-level access required</div>
                      <p>
                        Organization-wide AI figures need organization-level access. You can still open
                        the AI assets you have been given access to from a link or from My Tasks.
                      </p>
                    </div>
                  </div>
                </Card>
              </div>
              {/* The one org-wide read a scoped caller keeps, so they can judge whether what they
                  can open is current. */}
              <DiscoveryCoverageNote coverage={discoveryCoverage} />
              {expandedId != null && (
                <Card className="p-4">
                  {detailLoading ? (
                    <div className="flex items-center py-6 text-sm text-content-secondary">
                      <Loader className="w-4 h-4 mr-2 animate-spin" /> Loading evidence…
                    </div>
                  ) : detail ? (
                    <div data-testid="scoped-asset-detail">
                      <h2 className="text-base font-semibold text-content-primary mb-3">
                        {detail.asset?.name}
                      </h2>
                      <AssetDetail
                        detail={detail}
                        assetId={expandedId}
                        overrideAllowed={config?.responseOverrideEnabled !== false}
                        irreversibleAllowed={config?.irreversibleImmediateAllowed === true}
                        onOpenAsset={openAssetFromGraph}
                        canManage={canManage}
                        onAssetChanged={() => refreshDetail(expandedId)}
                      />
                    </div>
                  ) : (
                    <p className="py-3 text-sm text-content-secondary">Could not load asset detail.</p>
                  )}
                </Card>
              )}
            </>
          )
        ) : estateNature === "pending" ? (
          <Card className="overflow-hidden">
            <div className="flex items-center justify-center py-16 text-content-secondary">
              <Loader className="w-5 h-5 mr-2 animate-spin" /> Loading AI asset inventory…
            </div>
          </Card>
        ) : estateNature === "failed" ? (
          <Card className="p-6">
            <div role="alert" className="flex items-start gap-2 text-sm text-content-primary">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
              <span>Axaris could not confirm this organization's AI configuration. Reload to try again.</span>
            </div>
          </Card>
        ) : (
        <>
        {/* Above the tab strip, on every tab including settings: inside a tab it would vanish on
            the first click. Not dismissible — it describes the data, not the page. */}
        {estateNature === "constructed" && <ConstructedEstateBanner />}
        <AiAskDrawer
          open={askOpen}
          onClose={() => setAskOpen(false)}
          orgId={orgId}
          assetId={expandedId}
          tab={viewTab}
        />

        {viewTab === "settings" ? (
          <SettingsTab
            config={config}
            setConfig={setConfig}
            connections={connections}
            awsConnectors={awsConnectors}
            grcConnectors={grcConnectors}
            canManage={canManage}
            canAdminister={canAdminister}
            saving={savingConfig}
            onSave={saveConfig}
            onSavePolicy={savePolicy}
            savingPolicy={savingPolicy}
            policyNotice={policyNotice}
            dirty={configDirty}
            onClose={closeSettings}
          />
        ) : viewTab === "graph" ? (
          <>
            <ViewTabs tab={viewTab} setTab={switchTab} attention={attention} showPortfolio={canRunAgent(orgId)} />
            <EstateGraphTab onOpenAsset={openAssetFromGraph} />
          </>
        ) : viewTab === "usage" ? (
          <>
            <ViewTabs tab={viewTab} setTab={switchTab} attention={attention} showPortfolio={canRunAgent(orgId)} />
            <AiUsageCostPanel
              orgId={orgId}
              modelUsage={canViewAll ? summary?.modelUsage : undefined}
              modelUsageUnavailableReason={canViewAll ? summary?.modelUsageUnavailableReason : undefined}
            />
          </>
        ) : viewTab === "tiers" ? (
          <>
            <ViewTabs tab={viewTab} setTab={switchTab} attention={attention} showPortfolio={canRunAgent(orgId)} />
            {summary?.scopeNote && (
              <p data-testid="tiers-scope-note" className="mb-2 text-[11px] text-content-secondary">
                {summary.scopeNote}
              </p>
            )}
            <AiTiersTab
              assets={allAssets}
              config={config}
              canManage={canManage}
              savingTierId={savingTierId}
              onSaveTier={saveTier}
              onOpenAsset={openAssetFromGraph}
              spendByAsset={spendByAsset}
              spendUnavailable={spendUnavailable}
            />
          </>
        ) : viewTab === "respond" ? (
          <>
            <ViewTabs tab={viewTab} setTab={switchTab} attention={attention} showPortfolio={canRunAgent(orgId)} />
            <AiRespondTab
              onOpenAsset={openAssetFromGraph}
              refreshKey={config?.lastScannedAt}
              overrideAllowed={config?.responseOverrideEnabled !== false}
              tier1ImmediateAllowed={config?.tier1ImmediateAllowed === true}
              irreversibleAllowed={config?.irreversibleImmediateAllowed === true}
            />
          </>
        ) : viewTab === "portfolio" ? (
          <>
            <ViewTabs tab={viewTab} setTab={switchTab} attention={attention} showPortfolio={canRunAgent(orgId)} />
            <AiFleetPortfolioPanel orgId={orgId} refreshKey={config?.lastScannedAt} />
          </>
        ) : viewTab === "leaks" ? (
          <>
            <ViewTabs tab={viewTab} setTab={switchTab} attention={attention} showPortfolio={canRunAgent(orgId)} />
            <AiDataLeaksPanel orgId={orgId} refreshKey={config?.lastScannedAt} onOpenAsset={openAssetFromGraph} />
          </>
        ) : viewTab === "overview" ? (
          <>
            <ViewTabs tab={viewTab} setTab={switchTab} attention={attention} showPortfolio={canRunAgent(orgId)} />

            {/* Executive briefing first — the reader it exists for starts here. */}
            {canManage && (
              <IntelligenceNarrative
                type="aiintelligence"
                title="Intelligence Narrative"
                subtitle="AI-generated briefing on your AI estate and its risks"
                accentColor="purple"
              />
            )}

            {/* Above the metrics, not beside them: it qualifies the figures it sits on. Read-level,
                so not gated on canManage — whoever sees the counts sees whether they are current. */}
            <DiscoveryCoverageNote coverage={discoveryCoverage} />

            {/* Four headline metrics — the numbers a decision hangs on. Everything else on this
                tab explains one of them. */}
            <div data-testid="overview-counts" className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              {summaryCards.map(({ label, value, reason, Icon, tone }) => (
                <Card key={label} className="p-4">
                  <div className="flex items-center gap-2 text-content-secondary text-xs">
                    <Icon className={`w-4 h-4 ${tone}`} /> {label}
                  </div>
                  {reason ? (
                    <div className="text-xs text-content-secondary mt-1">{reason}</div>
                  ) : (
                    <div className={`text-2xl font-bold mt-1 ${tone}`}>{value}</div>
                  )}
                </Card>
              ))}
            </div>
            {/* Server-authored and always present: says what the counts above include and exclude
                (private deals the viewer cannot see), so a filtered number is not read as the
                whole estate. Rendered verbatim, never conditional on hidden deals. */}
            {summary?.scopeNote && (
              <p data-testid="scope-note" className="-mt-2 mb-4 text-[11px] text-content-secondary">
                {summary.scopeNote}
              </p>
            )}

            {/* Charts directly under the metrics (PM, 2026-08-24) — the work queue moved to its
                own Needs-attention tab, whose badge keeps the counts visible from here. */}
            {(summary?.totalAssets ?? 0) > 0 && (
              <AiIntelligenceCharts
                summary={summary}
                onAssetClick={openAssetFromChart}
                // Any tier slice opens the Tiers tab — the untiered slice is the work queue.
                onSelectTier={() => switchTab("tiers")}
              />
            )}

            {(summary?.totalAssets ?? 0) > 0 && (
              <div className="mb-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <EstateGrowthChart summary={summary} />
                <SpendTrajectoryChart orgId={orgId} />
              </div>
            )}

            {/* Below the two charts rather than inside the grid, and closed until asked for: one
                read runs about 35 queries. Not mounted without the capability. */}
            {canViewAll && (
              <AiEstateChangePanel
                refreshKey={config?.lastScannedAt}
                onOpenType={(filter) => {
                  setChangeFilter(filter);
                  switchTab("attention");
                }}
              />
            )}

            {/* The module's own posture score — collapsed to its headline. */}
            <AiPosturePanel refreshKey={config?.lastScannedAt} />

          </>
        ) : viewTab === "attention" ? (
          <ViewTabs tab={viewTab} setTab={switchTab} attention={attention} showPortfolio={canRunAgent(orgId)} />
        ) : (
          <>
            <ViewTabs tab={viewTab} setTab={switchTab} attention={attention} showPortfolio={canRunAgent(orgId)} />

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search assets…"
                className="border border-border rounded-md px-2 py-1.5 text-sm bg-theme-secondary text-content-primary w-48"
              />
              <select
                value={filterKind}
                onChange={(e) => setFilterKind(e.target.value)}
                className="border border-border rounded-md px-2 py-1.5 text-sm bg-theme-secondary text-content-primary"
              >
                <option value="">All kinds</option>
                {Object.entries(KIND_META).map(([k, m]) => (
                  <option key={k} value={k}>{m.label}</option>
                ))}
              </select>
              <select
                value={filterGovernance}
                onChange={(e) => setFilterGovernance(e.target.value)}
                className="border border-border rounded-md px-2 py-1.5 text-sm bg-theme-secondary text-content-primary"
              >
                <option value="">Governed + Shadow</option>
                <option value="Governed">Governed</option>
                <option value="Shadow">Shadow</option>
              </select>
              {summary?.staleAssets > 0 && (
                <label className="flex items-center gap-1.5 text-xs text-content-secondary cursor-pointer">
                  <input type="checkbox" checked={showStale} onChange={(e) => setShowStale(e.target.checked)} />
                  Show {summary.staleAssets} stale asset{summary.staleAssets === 1 ? "" : "s"} no longer seen by discovery
                </label>
              )}
            </div>

            {/* Inventory */}
            {summary?.scopeNote && (
              <p data-testid="inventory-scope-note" className="mb-2 text-[11px] text-content-secondary">
                {summary.scopeNote}
              </p>
            )}
            {spendUnavailable && (
              <div className="mb-2 text-[11px] text-amber-700">
                Spend is unavailable right now — the ledger read failed, so rows show no figures.
                Blank does not mean zero.
              </div>
            )}
            <Card className="overflow-hidden">
              {loading ? (
                <div className="flex items-center justify-center py-16 text-content-secondary">
                  <Loader className="w-5 h-5 mr-2 animate-spin" /> Loading AI asset inventory…
                </div>
              ) : visibleAssets.length === 0 ? (
                (summary?.totalAssets ?? 0) + (summary?.staleAssets ?? 0) > 0 ? (
                  <div className="text-center py-16 px-6">
                    <Brain className="w-10 h-10 mx-auto text-content-secondary mb-3" />
                    <p className="text-content-primary font-medium">No AI assets match the current filters</p>
                    <p className="text-sm text-content-secondary mt-1">
                      {summary.totalAssets} asset{summary.totalAssets === 1 ? "" : "s"} discovered
                      {filterGovernance === "Governed" && summary.governedAssets === 0
                        ? " — none are governed yet. An asset is Governed once it has a registered Entra agent identity and an accountable owner or sponsor."
                        : "."}
                    </p>
                    <button
                      onClick={() => { setFilterKind(""); setFilterGovernance(""); setSearch(""); }}
                      className="mt-3 text-sm text-purple-700 hover:text-purple-900 font-medium"
                    >
                      Clear filters
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-16 px-6">
                    <Brain className="w-10 h-10 mx-auto text-content-secondary mb-3" />
                    <p className="text-content-primary font-medium">No AI assets discovered yet</p>
                    <p className="text-sm text-content-secondary mt-1">
                      Connect your Microsoft tenant under Settings → Integrations, enable discovery in the
                      settings tab here, then run a scan.
                    </p>
                  </div>
                )
              ) : (
                <div className="divide-y divide-border">
                  {visibleAssets.map((asset) => {
                    const kindMeta = KIND_META[asset.kind] || KIND_META.Application;
                    const isOpen = expandedId === asset.id;
                    return (
                      <div key={asset.id}>
                        {/* A div, not a button: the badge strip now carries the tier pulldown,
                            and a select cannot legally nest inside a button. The tier controls
                            stop propagation; everywhere else still expands. */}
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => toggleExpand(asset)}
                          onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleExpand(asset); } }}
                          className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-theme-hover cursor-pointer"
                        >
                          {isOpen ? <ChevronDown className="w-4 h-4 flex-shrink-0 text-content-secondary" />
                                  : <ChevronRight className="w-4 h-4 flex-shrink-0 text-content-secondary" />}
                          <kindMeta.Icon className="w-5 h-5 flex-shrink-0 text-purple-700" />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-content-primary truncate">{asset.name}</div>
                            <div className="text-xs text-content-secondary flex items-center gap-2 mt-0.5">
                              <span>
                                {/* Agency comes from the asset now, not inferred from evidence
                                    confidence. Those are different questions: how sure we are the
                                    thing exists, versus how independently it acts. */}
                                {describeAsset(kindMeta.label, asset)}
                              </span>
                              {/* Ownerless stays inline — it is a core finding, not metadata.
                                  Owner names, last-seen, and activity moved to the expanded pane:
                                  eight signals per row was the review's headline complaint. */}
                              {!asset.ownerUpn && !asset.sponsorName && (
                                <span className="text-orange-600">No owner</span>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            {/* Tier first in the strip (Mark, 2026-08-25): a pulldown rather than
                                a pill so setting it never needs the pane open. Saves in place —
                                disable, PUT, patch the row — no page or asset refresh. The tier
                                explanation is the pulldown's own hover: a per-row (?) icon
                                duplicated down the list, and the help belongs on the control
                                the decision is made with. */}
                            <span
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                              title={"Criticality is declared by a risk manager, never inferred.\n\nTier 1 — most critical: response actions require a second person's approval by default (an administrator can waive this per org in settings).\nTier 2 — standard: the organization's response settings apply.\nTier 3 — sandbox / experimental.\nUntiered — behaves exactly as if tiers did not exist.\n\nWhat each tier means here is on the Tiers tab. Every change is recorded on the asset's trail and announced in What changed."}
                            >
                              {canManage ? (
                                <select
                                  value={asset.tier ?? ""}
                                  disabled={savingTierId === asset.id}
                                  onChange={(e) => saveTier(asset, e.target.value)}
                                  className={`text-[11px] font-medium rounded px-1 py-0.5 border bg-theme-primary disabled:opacity-60 ${
                                    asset.tier === 1
                                      ? "border-purple-300 text-purple-800 bg-purple-50"
                                      : "border-border text-content-secondary"
                                  }`}
                                >
                                  <option value="">Untiered</option>
                                  <option value="1">Tier 1</option>
                                  <option value="2">Tier 2</option>
                                  <option value="3">Tier 3</option>
                                </select>
                              ) : (
                                // AX-268: a read-only caller sees the tier, not a disabled control.
                                <span
                                  data-testid={`tier-label-${asset.id}`}
                                  className={`text-[11px] font-medium rounded px-1 py-0.5 border ${
                                    asset.tier === 1
                                      ? "border-purple-300 text-purple-800 bg-purple-50"
                                      : "border-border text-content-secondary"
                                  }`}
                                >
                                  {asset.tier ? `Tier ${asset.tier}` : "Untiered"}
                                </span>
                              )}
                            </span>
                            {asset.status !== "Active" && (
                              <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-500 border border-gray-300">
                                Stale — no longer seen
                              </span>
                            )}
                            {spendByAsset[asset.id]?.length > 0 && (
                              <span
                                className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-emerald-50 text-emerald-800 border border-emerald-300"
                                title="Billed spend the ledger attributes to this asset over the last 30 days — complete days only, so a floor. An agent with no figure usually bills through its provider's project or workspace, which carries the figure as its own asset."
                              >
                                {formatSpend(spendByAsset[asset.id])} / 30d
                              </span>
                            )}
                            {/* One primary source; the rest roll up. The full list lives in the
                                expanded pane. */}
                            {(asset.sources || []).slice(0, 1).map((s) => (
                              <span
                                key={s}
                                title={(asset.sources || []).map((src) => SOURCE_DISPLAY[src] ?? src).join(", ")}
                                className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${SOURCE_BADGE[s] || SOURCE_BADGE.GitHub}`}
                              >
                                {SOURCE_DISPLAY[s] ?? s}{(asset.sources || []).length > 1 ? ` +${asset.sources.length - 1}` : ""}
                              </span>
                            ))}
                            {asset.containmentState === "Contained" ? (
                              <span
                                title="A provider scan observes this identity's containment (disabled, quarantined or keys deactivated). Whether credentials issued before it still work is shown on its response action. Its posture risks are closed while the containment stands."
                                className="px-1.5 py-0.5 rounded text-[11px] font-medium bg-amber-50 text-amber-800 border border-amber-300"
                              >
                                Containment applied
                              </span>
                            ) : (asset.isOverPrivileged || asset.isRiskyAgent) && (
                              <span className="px-1.5 py-0.5 rounded text-[11px] font-medium bg-red-100 text-red-700 border border-red-200">
                                Over-privileged
                              </span>
                            )}
                            {aiIntelPreview && asset.dataLeakFindings > 0 && (
                              /* Step 11: Microsoft detected sensitive data reaching this app. A
                                 fact about the app, shown where its governance state is — it is
                                 usually the reason a shadow app matters. */
                              <span
                                className="px-1.5 py-0.5 rounded text-[11px] font-medium bg-amber-100 text-amber-800 border border-amber-200"
                                title="Microsoft Purview DLP or Defender for Cloud Apps detected sensitive data shared with this app"
                              >
                                {asset.dataLeakFindings} data leak{asset.dataLeakFindings === 1 ? "" : "s"}
                              </span>
                            )}
                            <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
                              asset.governanceState === "Governed"
                                ? "bg-green-50 text-green-700 border border-green-200"
                                : "bg-red-50 text-red-700 border border-red-200"
                            }`}>
                              {asset.governanceState === "Governed"
                                ? (<span className="flex items-center gap-1"><Eye className="w-3 h-3" />Governed</span>)
                                : (<span className="flex items-center gap-1"><EyeOff className="w-3 h-3" />Shadow</span>)}
                            </span>
                          </div>
                        </div>

                        {isOpen && (
                          // Boxed like the /risks drill-in: the pane now holds four tabs, and an
                          // unbounded panel made it unclear where the asset's detail ended.
                          // No tint behind the card: a square-cornered grey band around a
                          // rounded white box reads as a rendering artifact. The border and the
                          // gap carry the expansion instead.
                          <div className="px-11 pb-5 pt-3">
                          <div className="rounded-xl border border-border bg-theme-card-solid p-4">
                            {/* The facts the row used to carry — owner, sources, confidence,
                                recency — now live here, where someone has already chosen to
                                look at this asset. */}
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pb-3 mb-3 border-b border-border text-xs text-content-secondary">
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {asset.ownerUpn || asset.sponsorName || <span className="text-orange-600">No owner</span>}
                                {(() => {
                                  // An OpenAI agent's owner is inherited from its project, or kept
                                  // from an earlier scan when this scan could not confirm it.
                                  const provenance = asset.ownerUpn && detail ? ownerProvenance(detail.signals) : null;
                                  if (!provenance) return null;
                                  return provenance.kind === "project" ? (
                                    <span className="opacity-80" title="Inherited from the OpenAI project's owner">
                                      {" "}· from project {provenance.projectName}
                                    </span>
                                  ) : (
                                    <span
                                      className="opacity-80"
                                      title="The latest scan could not confirm this agent's project owner, so the owner found earlier was kept"
                                    >
                                      {" "}· from an earlier scan
                                    </span>
                                  );
                                })()}
                              </span>
                              {(() => {
                                // Whether Axaris can see the agents in this OpenAI project.
                                const coverage = detail ? agentCoverage(detail.signals) : null;
                                if (!coverage) return null;
                                return (
                                  <span className={coverage.status === "covered" ? "text-content-secondary" : "text-amber-700"}>
                                    {AGENT_COVERAGE_LABEL[coverage.status]}
                                  </span>
                                );
                              })()}
                              {(() => {
                                // A carried-forward signal bumps the asset's last-seen time to the
                                // latest scan; say it was not re-read rather than overstate it.
                                const notReRead = detail ? assetNotReReadSince(detail.signals) : null;
                                return notReRead ? (
                                  <span
                                    className="flex items-center gap-1 text-amber-700"
                                    title="The provider could not be read in the latest scan, so this asset was kept rather than re-checked. See the Evidence tab."
                                  >
                                    <Clock className="w-3 h-3" />
                                    {notReReadLabel(notReRead.lastConfirmedAt)}
                                  </span>
                                ) : (
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    Last seen {timeAgo(asset.lastSeenAt)}
                                  </span>
                                );
                              })()}
                              {asset.lastActivity && (
                                <span
                                  className="flex items-center gap-1"
                                  title={`${asset.lastActivity.summary}${asset.lastActivity.actorUserName ? ` — ${asset.lastActivity.actorUserName}` : ""} · ${new Date(asset.lastActivity.occurredAt).toLocaleString()}`}
                                >
                                  <History className="w-3 h-3" />
                                  {ACTIVITY_LABEL[asset.lastActivity.activityType] || "Action recorded"}
                                  {` · ${timeAgo(asset.lastActivity.occurredAt)}`}
                                </span>
                              )}
                              <span className="flex items-center gap-1.5">
                                {(asset.sources || []).map((s) => (
                                  <span key={s} className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${SOURCE_BADGE[s] || SOURCE_BADGE.GitHub}`}>
                                    {SOURCE_DISPLAY[s] ?? s}
                                  </span>
                                ))}
                              </span>
                              {asset.confidence && asset.confidence !== "Confirmed" && (
                                <span
                                  title="Discovery confidence — how strongly the evidence supports this asset's classification"
                                  className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${CONFIDENCE_CHIP[asset.confidence] || CONFIDENCE_CHIP.Unverified}`}
                                >
                                  {asset.confidence}
                                </span>
                              )}
                            </div>
                            {detailLoading ? (
                              <div className="flex items-center py-4 text-sm text-content-secondary">
                                <Loader className="w-4 h-4 mr-2 animate-spin" /> Loading evidence…
                              </div>
                            ) : detail ? (
                              <AssetDetail
                                detail={detail}
                                assetId={asset.id}
                                overrideAllowed={config?.responseOverrideEnabled !== false
                                  && (asset.tier !== 1 || config?.tier1ImmediateAllowed === true)}
                                irreversibleAllowed={config?.irreversibleImmediateAllowed === true}
                                onOpenAsset={openAssetFromGraph}
                                canManage={canManage}
                                onAssetChanged={() => refreshDetail(asset.id)}
                              />
                            ) : (
                              <p className="py-3 text-sm text-content-secondary">Could not load asset detail.</p>
                            )}
                          </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>

          </>
        )}

        {/* Mounted (hidden) for callers with the estate capability, shown only on the
            Needs-attention tab: the children fetch on mount, and the counts they report feed the
            tab badge from every other view. Not mounted at all without the capability, since
            each child would only collect a 403. */}
        {canViewAll && (
          <div className={tab === "attention" ? "" : "hidden"}>
            <AiActionCenter
              canManage={canManage}
              onOpenAsset={openAssetFromGraph}
              onReviewed={loadData}
              refreshKey={config?.lastScannedAt}
              lastScannedAt={config?.lastScannedAt}
              onSummaryChange={setAttention}
              changeFilter={changeFilter}
              onClearChangeFilter={() => setChangeFilter(null)}
            />
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}

/** The constructed-estate banner. Same amber treatment as the Behavior 360 absence notes. */
function ConstructedEstateBanner() {
  return (
    <div
      role="note"
      data-testid="constructed-estate-banner"
      className="mb-4 flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3"
    >
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <span>
        <strong>Constructed reference data.</strong> This organization's AI estate is a constructed
        dataset, not observed evidence. Findings, scores and figures here are scenario outputs.
      </span>
    </div>
  );
}

const CONFIDENCE_ORDER = ["Confirmed", "Corroborated", "Probable", "Suspected", "Declared", "Unverified"];

/** Overview, portfolio, needs-attention, inventory, respond, estate graph, usage. Settings stays on the
    gear button, not in here. The attention badge is what makes a non-default tab acceptable
    for the work queue: the count stays visible from every view, amber while decisions wait. */
function ViewTabs({ tab, setTab, attention, showPortfolio }) {
  // Data leaks is a preview surface (Features:AiIntelPreview); Portfolio stays visible.
  const aiIntelPreview = useAiIntelPreview();
  const tabClass = (name) =>
    `px-3 py-1.5 text-sm rounded-md border ${
      tab === name
        ? "border-border bg-theme-hover text-content-primary font-medium"
        : "border-transparent text-content-secondary hover:bg-theme-hover"
    }`;
  return (
    <div className="flex items-center gap-1 mb-4">
      <button onClick={() => setTab("overview")} className={tabClass("overview")}>Overview</button>
      {/* showPortfolio is canRunAgent(orgId) at every call site: both org-wide endpoints (portfolio,
          data-leaks) exclude DEAL/BU/SELF scope. */}
      {showPortfolio && (
        <>
          <button onClick={() => setTab("portfolio")} className={tabClass("portfolio")}>Portfolio</button>
          {aiIntelPreview && (
            <button onClick={() => setTab("leaks")} className={tabClass("leaks")}>Data leaks</button>
          )}
        </>
      )}
      <button onClick={() => setTab("attention")} className={`flex items-center gap-1.5 ${tabClass("attention")}`}>
        Needs attention
        {(attention?.total ?? 0) > 0 && (
          <span className={`px-1.5 py-0.5 rounded-full text-[11px] font-semibold ${
            (attention?.decisionsWaiting ?? 0) > 0
              ? "bg-amber-100 text-amber-800 border border-amber-300"
              : "bg-gray-100 text-gray-600 border border-gray-300"
          }`}>
            {attention.total}
          </span>
        )}
      </button>
      <button onClick={() => setTab("inventory")} className={tabClass("inventory")}>Inventory</button>
      <button onClick={() => setTab("tiers")} className={tabClass("tiers")}>Tiers</button>
      <button onClick={() => setTab("respond")} className={tabClass("respond")}>Respond</button>
      <button onClick={() => setTab("graph")} className={tabClass("graph")}>Estate Graph</button>
      <button onClick={() => setTab("usage")} className={tabClass("usage")}>Usage &amp; Cost</button>
    </div>
  );
}

/**
 * Filters for a graph view. Options are derived from the graph in hand rather than hard-coded, so
 * the lists never offer a provider or grade that would return nothing.
 */
function GraphFilters({
  graph, filters, setFilters, includeGone, setIncludeGone, includeIsolated, setIncludeIsolated,
}) {
  const options = useMemo(() => {
    const nodes = graph?.nodes || [];
    const present = (pick) => [...new Set(nodes.map(pick).filter(Boolean))];
    return {
      kinds: FILTERABLE_KINDS.filter((k) => nodes.some((n) => n.nodeKind === k)),
      providers: present((n) => n.providerSystem).sort(),
      confidences: CONFIDENCE_ORDER.filter((c) => nodes.some((n) => n.confidence === c)),
    };
  }, [graph]);

  const set = (key) => (e) =>
    setFilters((f) => ({ ...f, [key]: e.target.value ? [e.target.value] : [] }));

  const selectClass =
    "border border-border rounded-md px-2 py-1 text-xs bg-theme-secondary text-content-primary";

  return (
    <div className="flex flex-wrap items-center gap-2">
      {options.kinds.length > 1 && (
        <select value={filters.kinds[0] || ""} onChange={set("kinds")} className={selectClass}>
          <option value="">All kinds</option>
          {options.kinds.map((k) => (
            <option key={k} value={k}>{KIND_LABEL(k)}</option>
          ))}
        </select>
      )}
      {options.providers.length > 1 && (
        <select value={filters.providers[0] || ""} onChange={set("providers")} className={selectClass}>
          <option value="">All providers</option>
          {options.providers.map((p) => (
            <option key={p} value={p}>{SOURCE_DISPLAY[p] ?? p}</option>
          ))}
        </select>
      )}
      {options.confidences.length > 1 && (
        <select value={filters.confidences[0] || ""} onChange={set("confidences")} className={selectClass}>
          <option value="">All confidence</option>
          {options.confidences.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      )}
      <label className="flex items-center gap-1.5 text-xs text-content-secondary cursor-pointer">
        <input
          type="checkbox"
          checked={includeGone}
          onChange={(e) => setIncludeGone(e.target.checked)}
        />
        Include expired relationships
      </label>
      {setIncludeIsolated && (
        <label className="flex items-center gap-1.5 text-xs text-content-secondary cursor-pointer">
          <input
            type="checkbox"
            checked={includeIsolated}
            onChange={(e) => setIncludeIsolated(e.target.checked)}
          />
          Show unconnected assets
        </label>
      )}
    </div>
  );
}

const EDGE_TYPE_LABEL = {
  RunsAs: "Runs as",
  UsesModel: "Uses model",
  HasTool: "Has tool",
  AccessesData: "Accesses",
  DeployedFrom: "Deployed from",
  InProject: "In project",
};

/**
 * Provenance for whatever is selected in the graph. For an edge this answers "why does Axaris
 * believe this relationship exists" — which sources asserted it, at what confidence, and the
 * evidence signals behind it.
 */
export function GraphSelectionPanel({
  selection, nodesById, signals = [], onClear, onOpenAsset, currentAssetId,
}) {
  if (!selection) {
    return (
      <div className="mt-1.5 text-[11px] text-content-secondary">
        Select an edge to see why Axaris believes it, or a node for its details.
      </div>
    );
  }

  if (selection.type === "node") {
    const { node } = selection;
    // Opening an asset is an explicit action rather than a side effect of clicking the node —
    // a click that jumps you somewhere else loses the graph you were reading.
    const canOpen = node.aiAssetId != null && node.aiAssetId !== currentAssetId && onOpenAsset;
    return (
      <SelectionCard title={node.name} subtitle={KIND_LABEL(node.nodeKind)} onClear={onClear}>
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
          {node.providerSystem && <Field label="Provider" value={SOURCE_DISPLAY[node.providerSystem] ?? node.providerSystem} />}
          {node.confidence && <Field label="Confidence" value={node.confidence} />}
          {node.capabilityClass && <Field label="Capability" value={node.capabilityClass} />}
          {node.canonicalModelId && <Field label="Model family" value={node.canonicalModelId} />}
          <Field label="First seen" value={timeAgo(node.firstSeenAt)} />
          <Field label="Last seen" value={node.status === "Stale" ? "no longer seen" : timeAgo(node.lastSeenAt)} />
        </dl>
        {canOpen && (
          <button
            onClick={() => onOpenAsset(node.aiAssetId)}
            className="mt-2 flex items-center gap-1 text-[11px] font-medium text-purple-700 hover:text-purple-900"
          >
            Open in inventory <ExternalLink className="w-3 h-3" />
          </button>
        )}
      </SelectionCard>
    );
  }

  const { edge } = selection;
  const from = nodesById[edge.fromNodeId];
  const to = nodesById[edge.toNodeId];
  // Evidence can name signals belonging to a neighbouring asset, which this pane never loaded —
  // show what resolves and account for the rest rather than silently dropping them.
  const resolved = (edge.evidenceSignalIds || [])
    .map((id) => signals.find((s) => s.id === id))
    .filter(Boolean);
  const unresolved = (edge.evidenceSignalIds || []).length - resolved.length;

  return (
    <SelectionCard
      title={`${from?.name ?? "?"} → ${to?.name ?? "?"}`}
      subtitle={EDGE_TYPE_LABEL[edge.edgeType] || edge.edgeType}
      onClear={onClear}
    >
      <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
        <Field
          label={edge.sourceCount > 1 ? `Asserted by ${edge.sourceCount} sources` : "Asserted by"}
          value={edge.sources.map(signalSourceLabel).join(", ")}
        />
        {edge.confidence && <Field label="Confidence" value={edge.confidence} />}
        <Field label="First seen" value={timeAgo(edge.firstSeenAt)} />
        <Field
          label="Last seen"
          value={edge.status === "Gone" ? "no longer seen" : timeAgo(edge.lastSeenAt)}
        />
      </dl>
      {(resolved.length > 0 || unresolved > 0) && (
        <div className="mt-1.5">
          <div className="text-[11px] font-medium text-content-secondary">Evidence</div>
          <ul className="mt-0.5 space-y-0.5">
            {resolved.map((s) => (
              <li key={s.id} className="text-[11px] text-content-primary">
                {signalSourceLabel(s.sourceSystem)} · {s.signalType} — {s.title}
              </li>
            ))}
            {unresolved > 0 && (
              <li className="text-[11px] text-content-secondary">
                {resolved.length > 0
                  ? `${unresolved} more signal${unresolved === 1 ? "" : "s"} on a connected asset`
                  : `${unresolved} evidence signal${unresolved === 1 ? "" : "s"} — open the asset to read them`}
              </li>
            )}
          </ul>
        </div>
      )}
    </SelectionCard>
  );
}

function SelectionCard({ title, subtitle, onClear, children }) {
  return (
    <div className="mt-1.5 rounded-md border border-border bg-theme-secondary p-2.5">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-semibold text-content-primary">{title}</div>
          <div className="text-[11px] uppercase tracking-wide text-content-secondary">{subtitle}</div>
        </div>
        <button
          onClick={onClear}
          className="text-[11px] text-content-secondary hover:text-content-primary"
        >
          Clear
        </button>
      </div>
      <div className="mt-1.5 text-xs text-content-primary">{children}</div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <>
      <dt className="text-content-secondary">{label}</dt>
      <dd className="text-content-primary">{value}</dd>
    </>
  );
}

/**
 * The whole estate as one graph. Server-capped by connectedness, so a busy tenant shows its
 * most-connected core rather than an unreadable hairball — and says so when it has been cut.
 */
function EstateGraphTab({ onOpenAsset }) {
  const apiCall = useApi();
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(true);
  const [includeGone, setIncludeGone] = useState(false);
  const [includeIsolated, setIncludeIsolated] = useState(false);
  const [filters, setFilters] = useState({ kinds: [], providers: [], confidences: [] });
  const [selection, setSelection] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiCall(
      "GET",
      `/ai-assets/graph?includeGone=${includeGone}&includeIsolated=${includeIsolated}`
    )
      .then((g) => { if (!cancelled) setGraph(g); })
      .catch(() => { if (!cancelled) setGraph(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeGone, includeIsolated]);

  useEffect(() => { setSelection(null); }, [graph, filters]);

  const graphWithheld = isWithheld(graph);
  const filtered = useMemo(() => filterGraph(graphWithheld ? null : graph, filters), [graph, filters, graphWithheld]);
  const nodesById = useMemo(
    () => Object.fromEntries((filtered?.nodes || []).map((n) => [n.id, n])),
    [filtered]
  );

  const handleNodeClick = (node) => {
    if (node) setSelection({ type: "node", node });
  };

  if (loading) {
    return (
      <Card className="flex items-center justify-center py-16 text-content-secondary">
        <Loader className="w-5 h-5 mr-2 animate-spin" /> Building estate graph…
      </Card>
    );
  }

  // A withheld graph (private deals the caller cannot see) keeps its tab and says why.
  if (graphWithheld) {
    return (
      <Card className="p-4 mb-4">
        <div className="flex items-start gap-2 text-sm text-content-secondary">
          <Lock className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-content-primary">Estate graph withheld</div>
            <p>{asSentence(graph.reason)}</p>
          </div>
        </div>
      </Card>
    );
  }

  if (!graph?.nodes?.length) {
    const isolated = graph?.isolatedAssetCount ?? 0;
    return (
      <Card className="text-center py-16 px-6">
        <Brain className="w-10 h-10 mx-auto text-content-secondary mb-3" />
        <p className="text-content-primary font-medium">No relationships discovered yet</p>
        <p className="text-sm text-content-secondary mt-1">
          {isolated > 0
            ? `${isolated} asset${isolated === 1 ? " has" : "s have"} been discovered, but nothing connects them yet.`
            : "Relationships are built during discovery — run a scan, then come back."}
        </p>
        {isolated > 0 && (
          <button
            onClick={() => setIncludeIsolated(true)}
            className="mt-3 text-sm text-purple-700 hover:text-purple-900 font-medium"
          >
            Show unconnected assets
          </button>
        )}
      </Card>
    );
  }

  const hiddenByFilter = (graph.nodes?.length || 0) - (filtered.nodes?.length || 0);

  return (
    <Card className="p-3">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <GraphFilters
          graph={graph}
          filters={filters}
          setFilters={setFilters}
          includeGone={includeGone}
          setIncludeGone={setIncludeGone}
          includeIsolated={includeIsolated}
          setIncludeIsolated={setIncludeIsolated}
        />
        <div className="text-[11px] text-content-secondary">
          {filtered.nodes.length} node{filtered.nodes.length === 1 ? "" : "s"}
          {hiddenByFilter > 0 && ` · ${hiddenByFilter} hidden by filter`}
          {!includeIsolated && graph.isolatedAssetCount > 0 &&
            ` · ${graph.isolatedAssetCount} asset${graph.isolatedAssetCount === 1 ? "" : "s"} with no relationships`}
        </div>
      </div>

      {graph.truncated && (
        <div className="mb-2 flex items-start gap-1.5 rounded-md border border-yellow-200 bg-yellow-50 px-2.5 py-1.5 text-[11px] text-yellow-800">
          <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
          <span>
            Showing the {graph.nodes.length} most-connected of {graph.totalNodeCount} nodes. Open an
            individual asset to see its full neighbourhood.
          </span>
        </div>
      )}

      <div className="border border-border rounded-md bg-theme-primary" style={{ height: 560 }}>
        {filtered.nodes.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-content-secondary">
            No nodes match the current filters.
          </div>
        ) : (
          <AiIntelligenceGraph
            graph={filtered}
            selectedNodeId={selection?.type === "node" ? selection.node.id : null}
            selectedEdgeKey={selection?.type === "edge" ? edgeKey(selection.edge) : null}
            onNodeClick={handleNodeClick}
            onEdgeClick={(edge) => setSelection(edge ? { type: "edge", edge } : null)}
          />
        )}
      </div>

      <GraphSelectionPanel
        selection={selection}
        nodesById={nodesById}
        onClear={() => setSelection(null)}
        onOpenAsset={onOpenAsset}
      />
    </Card>
  );
}

/** One asset's neighbourhood, with the same filters and provenance panel as the estate view. */
function AssetRelationships({ assetId, signals, onOpenAsset, graph, includeGone, setIncludeGone }) {
  const [filters, setFilters] = useState({ kinds: [], providers: [], confidences: [] });
  const [selection, setSelection] = useState(null);

  // A re-fetch can replace the graph under a held selection, leaving a provenance card describing
  // an edge that no longer exists.
  useEffect(() => { setSelection(null); }, [graph, filters]);

  const filtered = useMemo(() => filterGraph(graph, filters), [graph, filters]);
  const nodesById = useMemo(
    () => Object.fromEntries((filtered?.nodes || []).map((n) => [n.id, n])),
    [filtered]
  );

  if (!graph?.nodes?.length) return null;

  const handleNodeClick = (node) => {
    if (node) setSelection({ type: "node", node });
  };

  const hiddenByFilter = graph.nodes.length - (filtered.nodes?.length || 0);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
        <div className="text-xs font-semibold text-content-secondary uppercase">Relationships</div>
        <GraphFilters
          graph={graph}
          filters={filters}
          setFilters={setFilters}
          includeGone={includeGone}
          setIncludeGone={setIncludeGone}
        />
      </div>
      <div className="border border-border rounded-md bg-theme-primary" style={{ height: 340 }}>
        {filtered.nodes.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-content-secondary">
            No nodes match the current filters.
          </div>
        ) : (
          <AiIntelligenceGraph
            graph={filtered}
            focusNodeId={graph.focusNodeId}
            selectedNodeId={selection?.type === "node" ? selection.node.id : null}
            selectedEdgeKey={selection?.type === "edge" ? edgeKey(selection.edge) : null}
            onNodeClick={handleNodeClick}
            onEdgeClick={(edge) => setSelection(edge ? { type: "edge", edge } : null)}
          />
        )}
      </div>
      {hiddenByFilter > 0 && (
        <div className="mt-1 text-[11px] text-content-secondary">
          {hiddenByFilter} node{hiddenByFilter === 1 ? "" : "s"} hidden by filter
        </div>
      )}
      {/* An emptied graph must not read as "this asset has no relationships". */}
      {!includeGone && graph.expiredEdgeCount > 0 && (
        <div className="mt-1 text-[11px] text-orange-600">
          {graph.expiredEdgeCount} relationship{graph.expiredEdgeCount === 1 ? "" : "s"} no longer
          observed by discovery.{" "}
          <button type="button" className="underline" onClick={() => setIncludeGone(true)}>
            Show expired
          </button>
        </div>
      )}
      <GraphSelectionPanel
        selection={selection}
        nodesById={nodesById}
        signals={signals}
        onClear={() => setSelection(null)}
        onOpenAsset={onOpenAsset}
        currentAssetId={assetId}
      />
    </div>
  );
}

/**
 * Owner editing for assets the customer declared they operate. Discovery-sourced owners are not
 * editable here — the next scan would overwrite the edit, so offering it would be a lie.
 */
function DeclaredOwnerEditor({ asset, nodeId, canManage, onSaved }) {
  const apiCall = useApi();
  const [editing, setEditing] = useState(false);
  const [ownerUpn, setOwnerUpn] = useState(asset.ownerUpn || "");
  const [saving, setSaving] = useState(false);

  // Local agents are sanctioned through LocalAgentSanctionEditor: their mirror node is not
  // claimable, so every edit offered here would be refused by the endpoint-ownership route.
  if (asset.isLocalAgent || !asset.isOwnerDeclared || !nodeId) return null;

  const save = async () => {
    setSaving(true);
    try {
      await apiCall("PUT", `/ai-assets/endpoints/${nodeId}/ownership`, {
        disposition: "Operated",
        ownerUpn: ownerUpn || null,
      });
      setEditing(false);
      onSaved?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="text-content-secondary">Declared owner:</span>
      {editing ? (
        <>
          <input
            value={ownerUpn}
            onChange={(e) => setOwnerUpn(e.target.value)}
            placeholder="owner@company.com"
            className="border border-border rounded-md px-2 py-1 text-xs bg-theme-secondary text-content-primary"
          />
          <button
            onClick={save}
            disabled={saving}
            className="font-medium text-purple-700 hover:text-purple-900 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
          <button
            onClick={() => { setEditing(false); setOwnerUpn(asset.ownerUpn || ""); }}
            className="text-content-secondary hover:text-content-primary"
          >
            Cancel
          </button>
        </>
      ) : (
        <>
          <span className={asset.ownerUpn ? "text-content-primary" : "text-orange-600"}>
            {asset.ownerUpn || "none set"}
          </span>
          {canManage && (
            <button
              onClick={() => setEditing(true)}
              className="font-medium text-purple-700 hover:text-purple-900"
            >
              {asset.ownerUpn ? "Change" : "Set owner"}
            </button>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Sanction for a local AI agent found on one of the customer's managed devices. The device is
 * theirs, so the question is not "do you operate this?" but whether the agent is sanctioned —
 * declared on the asset itself, never through endpoint ownership triage. One control owns every
 * transition: first declaration, naming or changing the owner, clearing it, and reversing.
 */
function LocalAgentSanctionEditor({ asset, canManage, onSaved }) {
  const apiCall = useApi();
  const [editing, setEditing] = useState(false);
  const [ownerUpn, setOwnerUpn] = useState(asset.ownerUpn || "");
  const [saving, setSaving] = useState(false);

  if (!asset.isLocalAgent || !asset.id) return null;

  const sanctioned = !!asset.isOwnerDeclared;

  const submit = async (declared, owner) => {
    setSaving(true);
    try {
      await apiCall("PUT", `/ai-assets/assets/${asset.id}/declaration`, {
        declared,
        ownerUpn: declared ? owner || null : null,
      });
      setEditing(false);
      onSaved?.();
    } catch (e) {
      alert(e?.response?.data?.message || "Could not update the sanction for this agent.");
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    setEditing(false);
    setOwnerUpn(asset.ownerUpn || "");
  };

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className="text-content-secondary">Sanction:</span>
      {editing ? (
        <>
          <input
            value={ownerUpn}
            onChange={(e) => setOwnerUpn(e.target.value)}
            placeholder="owner@company.com (optional)"
            className="border border-border rounded-md px-2 py-1 text-xs bg-theme-secondary text-content-primary"
          />
          <button
            onClick={() => submit(true, ownerUpn.trim())}
            disabled={saving}
            className="font-medium text-purple-700 hover:text-purple-900 disabled:opacity-50"
          >
            {saving ? "Saving…" : sanctioned ? "Save" : "Mark as sanctioned"}
          </button>
          <button onClick={cancel} className="text-content-secondary hover:text-content-primary">
            Cancel
          </button>
        </>
      ) : sanctioned ? (
        <>
          <span className="text-content-primary">Sanctioned</span>
          <span className="text-content-secondary">· owner</span>
          <span className={asset.ownerUpn ? "text-content-primary" : "text-orange-600"}>
            {asset.ownerUpn || "none set"}
          </span>
          {canManage && (
            <>
              <button
                onClick={() => setEditing(true)}
                disabled={saving}
                className="font-medium text-purple-700 hover:text-purple-900 disabled:opacity-50"
              >
                {asset.ownerUpn ? "Change owner" : "Set owner"}
              </button>
              {asset.ownerUpn && (
                <button
                  onClick={() => submit(true, null)}
                  disabled={saving}
                  className="text-content-secondary hover:text-content-primary disabled:opacity-50"
                >
                  Clear owner
                </button>
              )}
              <button
                onClick={() => submit(false, null)}
                disabled={saving}
                className="text-content-secondary hover:text-content-primary disabled:opacity-50"
              >
                Mark unsanctioned
              </button>
            </>
          )}
        </>
      ) : (
        <>
          <span className="text-orange-600">Not sanctioned</span>
          {canManage && (
            <button
              onClick={() => setEditing(true)}
              className="font-medium text-purple-700 hover:text-purple-900"
            >
              Mark as sanctioned
            </button>
          )}
        </>
      )}
    </div>
  );
}

function AssetDetail({ detail, assetId, onOpenAsset, canManage, onAssetChanged, overrideAllowed, irreversibleAllowed }) {
  const navigate = useNavigate();
  const apiCall = useApi();
  const aiIntelPreview = useAiIntelPreview();
  const { signals = [], risks = [], asset = {} } = detail;
  // Step 11: Microsoft's own DLP / Cloud Apps verdicts about data reaching this app. Evidence about
  // the app, not a posture rule — so they are listed apart from the posture risks above.
  const dataLeakFindings = detail.dataLeakFindings ?? [];
  const models = asset.models ?? [];
  const tools = asset.tools ?? [];
  const behaviorFeeds = detail.behaviorFeeds ?? [];
  const behaviorProfile = detail.behaviorProfile ?? null;
  const precedents = detail.precedents ?? [];
  const resolutionHistory = detail.resolutionHistory ?? [];
  // Both behavioral feeds read Entra, so they say nothing at all about an AWS role, an OpenAI
  // workspace or a bare repo signal. The feed status is per-org, so without this gate every asset
  // in the estate inherits an Entra diagnostic that does not apply to it.
  const entraBacked = !!asset.entraObjectId;
  // A behavioral feed covers this asset only if the asset has the identity that feed reads.
  const usageApplicable = entraBacked || !!asset.awsResourceArn || !!asset.gcpResourceName;
  const suppressions = detail.suppressions ?? [];
  // The asset's own mirror node, needed to address the ownership endpoint. Comes from the
  // subgraph the relationships panel already fetches, so no extra request.
  const [focusNodeId, setFocusNodeId] = useState(null);
  // Graph first: "what is this thing wired to" is the question the pane exists to answer, and it
  // is the one view that is never empty.
  const [detailTab, setDetailTab] = useState("graph");

  // Both async reads live here rather than in the children: a tab that renders its static half
  // first and pops the fetched half in a second later reads as broken, and hoisting them also
  // means switching tabs after the first load is instant.
  const [graph, setGraph] = useState(null);
  const [graphLoading, setGraphLoading] = useState(true);
  const [includeGone, setIncludeGone] = useState(false);
  const [response, setResponse] = useState(null);
  const [responseLoading, setResponseLoading] = useState(true);
  // Behavior 360 and Playbook are the lazy tabs: fetched on first activation, kept while valid.
  // null = no snapshot | {error:true} = failed | otherwise the DTO.
  const [behavior360, setBehavior360] = useState(null);
  const [behavior360Loading, setBehavior360Loading] = useState(false);
  const [playbook, setPlaybook] = useState(null);
  const [playbookLoading, setPlaybookLoading] = useState(false);
  // Same-asset invalidation token, bumped whenever `detail` refreshes. The refs record which
  // token a tab's snapshot was fetched under.
  const [detailVersion, setDetailVersion] = useState(0);
  const behavior360FetchedFor = useRef(-1);
  const playbookFetchedFor = useRef(-1);
  // The Activity tab's merged history. Read eagerly, not on tab open, because the tab label shows
  // its count; refetched whenever `detail` refreshes, since owner/tier/playbook saves add entries.
  const history = useAssetHistory(assetId, detail);
  // Entry paths: lazy on the Risks & Response tab, refetched when `detail` refreshes.
  const entryPaths = useEntryPaths(assetId, detailTab === "risks", detailVersion);

  // A different asset is a different subject — clear outright so old content cannot linger.
  useEffect(() => {
    setBehavior360(null);
    setPlaybook(null);
    behavior360FetchedFor.current = -1;
    playbookFetchedFor.current = -1;
  }, [assetId]);

  // A same-asset refresh (owner, tier, suppression, playbook save — everything that calls
  // onAssetChanged) marks the snapshots stale WITHOUT clearing them: the open tab refetches
  // with its content still mounted instead of flashing to a spinner, and a closed tab
  // refetches on its next open.
  useEffect(() => {
    setDetailVersion((v) => v + 1);
  }, [detail]);

  useEffect(() => {
    if (detailTab !== "behavior360") return undefined;
    if (behavior360 && behavior360FetchedFor.current === detailVersion) return undefined;
    // Cancellation, not just cleanup: an older response must never replace newer state after an
    // invalidation raced it.
    let cancelled = false;
    if (!behavior360) setBehavior360Loading(true); // spinner only when nothing is on screen yet
    apiCall("GET", `/ai-assets/assets/${assetId}/behavior360`)
      .then((d) => { if (!cancelled) { setBehavior360(d); behavior360FetchedFor.current = detailVersion; } })
      .catch(() => { if (!cancelled) { setBehavior360({ error: true }); behavior360FetchedFor.current = detailVersion; } })
      .finally(() => { if (!cancelled) setBehavior360Loading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailTab, behavior360, detailVersion, assetId]);

  useEffect(() => {
    if (detailTab !== "playbook") return undefined;
    if (playbook && playbookFetchedFor.current === detailVersion) return undefined;
    let cancelled = false;
    if (!playbook) setPlaybookLoading(true);
    apiCall("GET", `/ai-assets/assets/${assetId}/playbook`)
      .then((d) => { if (!cancelled) { setPlaybook(d); playbookFetchedFor.current = detailVersion; } })
      .catch(() => { if (!cancelled) { setPlaybook({ error: true }); playbookFetchedFor.current = detailVersion; } })
      .finally(() => { if (!cancelled) setPlaybookLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailTab, playbook, detailVersion, assetId]);

  useEffect(() => {
    let cancelled = false;
    setGraphLoading(true);
    apiCall("GET", `/ai-assets/assets/${assetId}/graph?includeGone=${includeGone}`)
      .then((g) => {
        if (cancelled) return;
        setGraph(g);
        // The asset's own node id — the ownership endpoint is addressed by node, and this graph
        // read is the only place that mapping is already available.
        setFocusNodeId(g?.focusNodeId ?? null);
      })
      .catch(() => { if (!cancelled) setGraph(null); })
      .finally(() => { if (!cancelled) setGraphLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetId, includeGone]);

  const loadResponse = useCallback(async () => {
    const [available, actions] = await Promise.all([
      apiCall("GET", `/ai-assets/response-actions/available/${assetId}`).catch(() => []),
      apiCall("GET", `/ai-assets/response-actions?assetId=${assetId}`).catch(() => []),
    ]);
    setResponse({
      available: Array.isArray(available) ? available : [],
      actions: Array.isArray(actions) ? actions : [],
    });
    setResponseLoading(false);
  }, [apiCall, assetId]);

  useEffect(() => { loadResponse(); }, [loadResponse]);

  // "Not a risk" is a suppression risk generation consults, not a close — a closed risk would be
  // reopened by the next scan while its finding still fires.
  const [suppressing, setSuppressing] = useState(null);
  const [suppressBusy, setSuppressBusy] = useState(false);

  const confirmSuppress = async (reason) => {
    setSuppressBusy(true);
    try {
      await apiCall("POST", `/ai-assets/assets/${assetId}/suppressions`, {
        ruleCode: suppressing.riskCategory,
        reason,
      });
      setSuppressing(null);
      onAssetChanged?.();
    } catch (e) {
      alert(e?.response?.data?.message || "Could not record that decision.");
    } finally {
      setSuppressBusy(false);
    }
  };

  const onWithdrawSuppression = async (suppressionId) => {
    try {
      await apiCall("POST", `/ai-assets/suppressions/${suppressionId}/withdraw`, {});
      onAssetChanged?.();
    } catch (e) {
      alert(e?.response?.data?.message || "Could not withdraw that decision.");
    }
  };

  const detailTabClass = (name) =>
    `px-3 py-1.5 text-sm rounded-md border whitespace-nowrap shrink-0 ${
      detailTab === name
        ? "border-border bg-theme-hover text-content-primary font-medium"
        : "border-transparent text-content-secondary hover:bg-theme-hover"
    }`;
  const tabCount = (n) => (n > 0 ? ` (${n})` : "");

  return (
    <div className="space-y-4 pt-2">
      {asset.isLocalAgent ? (
        <LocalAgentSanctionEditor
          key={asset.id}
          asset={asset}
          canManage={canManage}
          onSaved={onAssetChanged}
        />
      ) : (
        <DeclaredOwnerEditor
          asset={asset}
          nodeId={focusNodeId}
          canManage={canManage}
          onSaved={onAssetChanged}
        />
      )}
      {/* Above the tabs, beside the owner. Who operates an agent and which part of the business
          it belongs to are the same question asked twice, and neither is a property of any one
          tab — putting this under Risks made it look like a finding. */}
      <AiAssetBusinessAnchor
        asset={asset}
        businessAnchorName={detail.businessAnchorName}
        canManage={canManage}
        onChanged={onAssetChanged}
      />
      {/* Tabs rather than one long scroll: the pane now holds four unrelated questions (what is
          it wired to, what is wrong with it, why do we believe that, what have we done to it). */}
      {/* Five tabs now — scroll rather than clip when the pane is narrow. */}
      <div className="flex items-center gap-1 border-b border-border pb-2 overflow-x-auto">
        <button onClick={() => setDetailTab("graph")} className={detailTabClass("graph")}>Graph</button>
        <button onClick={() => setDetailTab("behavior360")} className={detailTabClass("behavior360")}>
          Behavior 360
        </button>
        <button onClick={() => setDetailTab("playbook")} className={detailTabClass("playbook")}>
          Playbook
        </button>
        <button onClick={() => setDetailTab("risks")} className={detailTabClass("risks")}>
          Risks &amp; Response{tabCount(risks.length)}
        </button>
        <button onClick={() => setDetailTab("evidence")} className={detailTabClass("evidence")}>
          Evidence{tabCount(signals.length)}
        </button>
        <button onClick={() => setDetailTab("activity")} className={detailTabClass("activity")}>
          Activity{historyTabCount(history)}
        </button>
      </div>
      {detailTab === "graph" && (graphLoading ? <DetailTabSpinner /> : <>
      {/* Models this asset uses, from its active UsesModel edges. Shown even when empty: an AI asset
          with no model attached is the signature of a discovery gap (the source found the asset but
          not what it runs), and silently omitting the section hides that. */}
      <div>
        <div className="text-xs font-semibold text-content-secondary uppercase mb-1.5">
          Models ({models.length})
        </div>
        {models.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {models.map((m) => (
              <span key={m} className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200 text-xs">
                {m}
              </span>
            ))}
          </div>
        ) : (
          <div className="text-xs text-content-secondary">
            No model linked to this asset yet — discovery found the asset but not what it runs.
          </div>
        )}
      </div>
      {/* Tools and data sources (HasTool + AccessesData edges, merged by ToolsFor). Unlike Models
          this section is hidden when empty: an asset with no tools is the normal case, not a
          discovery gap, so an always-on "none" line would be noise on most assets. */}
      {tools.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-content-secondary uppercase mb-1.5">
            Tools &amp; Data Sources ({tools.length})
          </div>
          <div className="flex flex-wrap gap-1.5">
            {tools.map((t) => (
              <span key={t} className="px-2 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200 text-xs">
                {t}
              </span>
            ))}
          </div>
        </div>
      )}
      <AssetRelationships
        assetId={assetId}
        signals={signals}
        onOpenAsset={onOpenAsset}
        graph={graph}
        includeGone={includeGone}
        setIncludeGone={setIncludeGone}
      />
      </>)}

      {detailTab === "behavior360" && (
        <>
          {behavior360Loading ? <DetailTabSpinner /> : (
            behavior360?.error ? (
              <div className="text-xs text-content-secondary">
                Behavior 360 could not be loaded for this asset.{" "}
                <button onClick={() => setBehavior360(null)} className="text-blue-600 hover:underline">
                  Retry
                </button>
              </div>
            ) : behavior360 ? (
              <AiBehavior360Panel data={behavior360} onShowTab={setDetailTab} showBoundary={aiIntelPreview} />
            ) : null
          )}
          {/* Discussion lives with the questions 360 raises — own fetch and state, deliberately
              outside the 360 cache invalidation. */}
          <AiAssetComments assetId={assetId} />
        </>
      )}

      {detailTab === "playbook" && (playbookLoading ? <DetailTabSpinner /> : (
        playbook?.error ? (
          <div className="text-xs text-content-secondary">
            The playbook could not be loaded for this asset.{" "}
            <button onClick={() => setPlaybook(null)} className="text-blue-600 hover:underline">
              Retry
            </button>
          </div>
        ) : playbook ? (
          <AiPlaybookPanel
            assetId={assetId}
            data={playbook}
            canManage={canManage}
            // A save changes the asset's declared state: the detail refresh this triggers bumps
            // the invalidation token, which refetches this tab in place (content stays mounted —
            // no flash) and invalidates Behavior 360's declared-purpose block.
            onSaved={() => onAssetChanged?.()}
          />
        ) : null
      ))}

      {/* Risks first, then what can be done about them — the reading order an operator wants. */}
      {detailTab === "risks" && (responseLoading ? <DetailTabSpinner /> : <>
      <AiEntryPathPanel state={entryPaths} asset={asset} risks={risks} />
      {risks.length === 0 && (
        <div className="text-xs text-content-secondary">No open risks on this asset.</div>
      )}
      {suppressions.filter((x) => x.status === "Active").length > 0 && (
        <div>
          <div className="text-xs font-semibold text-content-secondary uppercase mb-1.5">
            Recorded as not a risk
          </div>
          <div className="space-y-1.5">
            {suppressions.filter((x) => x.status === "Active").map((x) => (
              <div key={x.id} className="flex items-start gap-2 text-sm border border-border rounded-md p-2.5 bg-theme-primary">
                <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="text-content-primary">{RULE_LABEL[x.ruleCode] || x.ruleCode}</div>
                  <div className="text-xs text-content-secondary">
                    {x.reason} — {x.suppressedByUserName || "unknown"} · {new Date(x.suppressedAt).toLocaleDateString()}
                  </div>
                  <div className="text-xs text-content-secondary mt-0.5">
                    Returns automatically if the evidence behind it changes. Undoing re-raises it
                    on the next scan.
                  </div>
                </div>
                {canManage && (
                  <button
                    onClick={() => onWithdrawSuppression(x.id)}
                    className="text-xs px-2.5 py-1 border rounded-lg font-medium bg-theme-secondary text-content-secondary border-border hover:bg-theme-hover shrink-0"
                  >
                    Undo
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {risks.length > 0 && (
        <div>
          <div className="text-xs font-semibold text-content-secondary uppercase mb-1.5">Open Risks</div>
          <div className="space-y-1.5">
            {risks.map((r) => (
              <div
                key={r.unifiedRiskId}
                className="w-full flex items-center gap-2 text-sm rounded-md px-1.5 py-1 -mx-1.5 hover:bg-theme-hover"
              >
                <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${RATING_PILL[r.inherentRiskRating] || RATING_PILL.LOW}`}>
                  {r.inherentRiskRating}
                </span>
                <span className="text-content-primary flex-1">{r.title}</span>
                <span className="text-content-secondary text-xs">{formatImpact(r.estimatedMonetaryImpact)}</span>
                {/* Matches the GRC / Security Insights "View Risk" button */}
                {canManage && (
                  <button
                    onClick={() => setSuppressing(r)}
                    className="text-xs px-2.5 py-1 border rounded-lg font-medium transition-all bg-theme-secondary text-content-secondary border-border hover:bg-theme-hover active:scale-95"
                    title="Record that this finding does not apply. It returns if the evidence behind it changes."
                  >
                    Not a risk
                  </button>
                )}
                <button
                  onClick={() => navigate(`/risks?riskId=${r.unifiedRiskId}`)}
                  className="flex items-center gap-1.5 text-xs px-2.5 py-1 border rounded-lg font-medium transition-all bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 active:scale-95"
                  title="Open in the Risk Register — accept the risk, assign it, or track remediation there"
                >
                  <Eye className="w-3 h-3" />
                  View Risk
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      {aiIntelPreview && (
        <AiDataLeakFindings findings={dataLeakFindings} onOpen={(riskId) => navigate(`/risks?riskId=${riskId}`)} />
      )}
      <AiResponseActionsPanel
        assetId={assetId}
        assetName={asset.name}
        available={response?.available ?? []}
        actions={response?.actions ?? []}
        onChanged={loadResponse}
        overrideAllowed={overrideAllowed}
        irreversibleAllowed={irreversibleAllowed}
      />
      </>)}

      {detailTab === "activity" && (
        <>
          <AssetObservedUsage
            profile={behaviorProfile}
            feeds={behaviorFeeds}
            applicable={usageApplicable}
          />
          <AssetActivityTrail history={history} assetId={assetId} startExpanded />
          {/* Entra-only: the change feed says nothing about an AWS role or an OpenAI workspace. */}
          {entraBacked && (
            <BehaviorCoverageNote feed={behaviorFeeds.find((f) => f.sourceSystem === "ENTRA_AUDIT")} />
          )}
        </>
      )}

      <AiSuppressRiskModal
        isOpen={!!suppressing}
        risk={suppressing}
        isBusy={suppressBusy}
        onClose={() => setSuppressing(null)}
        onConfirm={confirmSuppress}
      />

      {detailTab === "evidence" && (
      <div>
        <div className="text-xs font-semibold text-content-secondary uppercase mb-1.5">
          Evidence ({signals.length} signal{signals.length === 1 ? "" : "s"})
        </div>
        <div className="space-y-2">
          {signals.map((s) => (
            <div key={s.id} className="border border-border rounded-md p-3 bg-theme-primary">
              <div className="flex items-center gap-2 text-sm">
                <span className="px-1.5 py-0.5 rounded text-[11px] font-medium bg-purple-50 text-purple-700 border border-purple-200">
                  {signalSourceLabel(s.sourceSystem)}
                </span>
                <span className="font-medium text-content-primary">{s.signalType}</span>
                {s.confidence && s.confidence !== "Confirmed" && (
                  <span className={`px-1.5 py-0.5 rounded text-[11px] font-medium ${CONFIDENCE_CHIP[s.confidence] || CONFIDENCE_CHIP.Unverified}`}>
                    {s.confidence}
                  </span>
                )}
                {(() => {
                  const notReRead = signalNotReReadSince(s);
                  return notReRead ? (
                    <span className="text-amber-700 text-xs ml-auto">
                      {notReReadLabel(notReRead.lastConfirmedAt)}
                    </span>
                  ) : (
                    <span className="text-content-secondary text-xs ml-auto">
                      {s.status === "Gone" ? "No longer seen" : `Last seen ${timeAgo(s.lastSeenAt)}`}
                    </span>
                  );
                })()}
              </div>
              {s.evidence && (
                <pre className="mt-2 text-xs text-content-secondary bg-theme-secondary rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">
                  {JSON.stringify(s.evidence, null, 2)}
                </pre>
              )}
            </div>
          ))}
        </div>
      </div>
      )}
    </div>
  );
}

/** One spinner per tab, so a tab is either loading or complete — never half-rendered. */
function DetailTabSpinner() {
  return (
    <div className="flex items-center py-6 text-sm text-content-secondary">
      <Loader className="w-4 h-4 mr-2 animate-spin" /> Loading…
    </div>
  );
}

/**
 * What the asset has actually been observed doing, from sign-in telemetry.
 *
 * The empty case is the important one. An agent with no observed authentications and an agent we
 * have no telemetry for render identically unless the component says which — and the difference is
 * between "this privilege is unused" and "we cannot see". Every number here is stated with the
 * window it was measured over, because "never" is only ever a claim about that window.
 */
function AssetObservedUsage({ profile, feeds, applicable }) {
  // Silence when no behavioral feed covers this asset class at all — an OpenAI workspace has
  // neither an Entra identity nor an IAM role, so "in use" and "cannot tell" are both statements
  // not worth making. The dormancy rule is silent on these too, so the UI matches the backend.
  if (!applicable) return null;

  // Whichever feed produced this asset's profile. Entra sign-ins and AWS role usage answer the
  // same question for different asset classes.
  const feed = feeds.find((f) => f.sourceSystem === profile?.sourceSystem)
    ?? feeds.find((f) => f.sourceSystem === "ENTRA_SIGNIN" || f.sourceSystem === "AWS_IAM"
      || f.sourceSystem === "GCP_IAM");

  if (!feed || feed.status !== "Healthy" || !profile) {
    return (
      <div className="mb-3 pb-3 border-b border-border text-xs text-content-secondary">
        <AlertTriangle className="w-3 h-3 inline mr-1 -mt-0.5 text-amber-600" />
        No usage telemetry for this asset, so Axaris cannot say whether it is in use.
        {feed?.message ? ` ${feed.message}` : ""}
        {/* A failure message with no timestamp reads as current however old it is — and this row
            survives until a later cycle overwrites it, so "will resume on the next cycle" can sit
            here for hours after the problem is gone. */}
        {feed?.lastPolledAt && (
          <span className="text-content-secondary"> Last attempted {timeAgo(feed.lastPolledAt)}.</span>
        )}
      </div>
    );
  }

  // AWS reports when the shared execution ROLE was last used, which is a weaker claim than an
  // identity signing in — the wording has to keep them apart.
  const isRole = profile.sourceSystem === "AWS_IAM";
  // Google Cloud reports when the service account the agent runs as last authenticated. Like the
  // AWS role, it carries no sign-in counts.
  const isServiceAccount = profile.sourceSystem === "GCP_IAM";

  const never = !profile.lastAuthenticatedAt;
  return (
    <div className="mb-3 pb-3 border-b border-border">
      <div className="text-xs font-semibold text-content-secondary uppercase mb-1.5">
        Observed usage
      </div>
      <div className="text-sm text-content-primary">
        {never ? (
          <span>
            {isServiceAccount
              ? `The service account this agent runs as has not authenticated in ${profile.observedDays} days.`
              : isRole
              ? `The role this agent runs as has not been used in ${profile.observedDays} days.`
              : `No authentication in the last ${profile.observedDays} days.`}
            <span className="text-content-secondary text-xs">
              {" "}Privilege this agent holds is currently unexercised.
            </span>
          </span>
        ) : (
          <span>
            {isServiceAccount
              ? "Service account last authenticated "
              : isRole ? "Role last used " : "Last authenticated "}{timeAgo(profile.lastAuthenticatedAt)}
            {!isRole && !isServiceAccount && (
              <span className="text-content-secondary text-xs">
                {" · "}{profile.signInCount} sign-in{profile.signInCount === 1 ? "" : "s"} in{" "}
                {profile.observedDays} days
                {profile.failedSignInCount > 0 && ` · ${profile.failedSignInCount} failed`}
              </span>
            )}
          </span>
        )}
      </div>
      {profile.resources?.length > 0 && (
        <div className="text-xs text-content-secondary mt-1">
          Resources: {profile.resources.slice(0, 6).join(", ")}
        </div>
      )}
    </div>
  );
}

/**
 * What this organization's own history says about the findings on this asset.
 *
 * Two things, both drawn from records that already existed and were never related to each other: a
 * finding that has been raised and resolved before, and a decision somebody took about the same
 * rule on a different asset. The second matters because without it the same judgement gets
 * re-litigated — or decided differently by someone who never saw the first one.
 */
function AssetResolutionHistory({ history, precedents }) {
  const recurring = (history ?? []).filter((h) => h.timesRaised > 0);
  if (recurring.length === 0 && (precedents ?? []).length === 0) return null;

  return (
    <div className="mb-3 pb-3 border-b border-border">
      {recurring.length > 0 && (
        <>
          <div className="text-xs font-semibold text-content-secondary uppercase mb-1.5">
            Resolution history
          </div>
          {recurring.map((h) => (
            <div key={h.ruleCode} className="text-sm text-content-primary">
              {h.ruleCode}
              <span className="text-content-secondary text-xs">
                {" — resolved "}{h.timesRaised} time{h.timesRaised === 1 ? "" : "s"} before
                {h.meanHoursToResolve
                  ? `, averaging ${Math.round(h.meanHoursToResolve / 24)} day(s)`
                  : ""}
                {h.exposureAvoided > 0
                  ? ` · ${Math.round(h.exposureAvoided).toLocaleString()} exposure avoided`
                  : ""}
              </span>
            </div>
          ))}
        </>
      )}

      {precedents.length > 0 && (
        <div className={recurring.length > 0 ? "mt-3" : ""}>
          <div className="text-xs font-semibold text-content-secondary uppercase mb-1.5">
            Decisions on other assets
          </div>
          {precedents.map((p, i) => (
            <div key={`${p.ruleCode}-${p.aiAssetId}-${i}`} className="text-sm text-content-primary">
              {p.assetName}
              <span className="text-content-secondary text-xs">
                {" — "}“{p.reason}”
                {p.decidedBy ? ` · ${p.decidedBy}` : ""}
                {" · "}{new Date(p.decidedAt).toLocaleDateString()}
              </span>
            </div>
          ))}
          <p className="text-[11px] text-content-secondary mt-1">
            Recorded for the same rule elsewhere in your estate. Consider whether the same reasoning
            applies here before deciding again.
          </p>
        </div>
      )}
    </div>
  );
}

/** A stored instant as a readable date, or null when absent, unparseable or at/before the epoch. */
function coverageDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime()) || d.getTime() <= 0) return null;
  return d.toLocaleString();
}

const COVERAGE_CANNOT_CONFIRM = "Axaris cannot confirm how current these figures are. They may "
  + "include AI assets that no longer exist, and miss AI assets that do.";

const COVERAGE_REFRESH_FAILED = "Axaris could not re-check its discovery sources just now, so it "
  + "cannot confirm how current these figures are. They may include AI assets that no longer "
  + "exist, and miss AI assets that do.";

/**
 * The coverage note plus, when the latest re-read failed, a warning above it. The previously
 * read answer stays rendered (a failed re-read never blanks it); the warning is added because
 * the counts refreshed without it, so even a prior "all current" answer can no longer vouch.
 */
function DiscoveryCoverageNote({ coverage }) {
  const answer = <DiscoveryCoverageAnswer coverage={coverage} />;
  if (coverage.read !== "ok" || !coverage.refreshFailed) return answer;
  return (
    <>
      <div
        role="status"
        data-testid="discovery-coverage-refresh-failed"
        className="mb-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3 flex items-start gap-2"
      >
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <span>{COVERAGE_REFRESH_FAILED}</span>
      </div>
      {answer}
    </>
  );
}

/**
 * Says, above the Overview counts, when those counts are not current.
 *
 * The counts are persisted Active assets, and a source that fails keeps its assets Active — so
 * the figures can at once include assets that were removed and miss assets that were added.
 * Without this note the headline reads as current when it is last-known. Silent only when every
 * source is current; every other answer, including a failed or unreadable status, is said.
 */
function DiscoveryCoverageAnswer({ coverage }) {
  if (coverage.read === "pending") return null;

  const amberBox = "mb-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3";
  const line = (text) => (
    <div role="status" data-testid="discovery-coverage-note" className={`${amberBox} flex items-start gap-2`}>
      <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
      <span>{text}</span>
    </div>
  );

  if (coverage.read === "failed") return line(COVERAGE_CANNOT_CONFIRM);
  const data = coverage.data;
  if (data.modulePaused) {
    return line("AI discovery is paused, so no source has been read since. These figures are as "
      + "of the pause: they may include AI assets that no longer exist, and miss AI assets added since.");
  }
  // Arrives as a 200 with no rows: an empty list here is NOT "everything is current".
  if (data.statusUnreadable) return line(COVERAGE_CANNOT_CONFIRM);

  const sources = data.sources;
  if (sources.length === 0) {
    // No rows and not flagged current is a contradiction; say so rather than reassure.
    return data.allSourcesCurrent === false ? line(COVERAGE_CANNOT_CONFIRM) : null;
  }

  const calm = sources.filter((s) => s.state === "NeverScanned");
  const amber = sources.filter((s) => s.state !== "NeverScanned"); // unknown states stay amber
  const stale = amber.filter((s) => s.state === "Failed" || s.state === "Partial");
  let header;
  if (stale.length > 0 && stale.every((s) => coverageDate(s.lastCompleteSuccessAt))) {
    header = "Some sources below could not be read in the last scan, so these figures reflect "
      + "their last successful read. They may include AI assets that no longer exist, and miss "
      + "AI assets added since.";
  } else if (amber.length > 0) {
    header = "Some sources below have not been read completely, so these figures may include AI "
      + "assets that no longer exist, and miss AI assets that do.";
  } else {
    header = "Some sources have not been read yet, so their AI assets are not in these figures yet.";
  }

  // The display name only, never the raw source code.
  const name = (s) => s.displayName || "A discovery source";
  const rowDetail = (s) => {
    const last = coverageDate(s.lastCompleteSuccessAt);
    const read = last ? `last read successfully ${last}` : "never read successfully";
    if (s.state === "Failed") return `Reading failed · ${read}`;
    if (s.state === "Partial") return `Read only in part · ${read}`;
    if (s.state === "NotConfigured") return "Not connected";
    if (s.state === "NeverScanned") return "Not read yet";
    return "Status unknown";
  };

  return (
    <div
      role="status"
      data-testid="discovery-coverage-note"
      className={amber.length > 0
        ? amberBox
        : "mb-4 text-sm text-content-secondary bg-theme-secondary border border-border rounded p-3"}
    >
      <div className="flex items-start gap-2">
        {amber.length > 0
          ? <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          : <Clock className="w-4 h-4 mt-0.5 shrink-0" />}
        <span className={amber.length > 0 ? "font-medium" : ""}>{header}</span>
      </div>
      <ul className="mt-2 ml-6 space-y-1.5">
        {amber.map((s) => (
          <li
            key={s.sourceSystem}
            data-testid={`coverage-row-${s.state}`}
            className={s.state === "NotConfigured"
              ? "text-amber-900 font-medium border-l-2 border-amber-500 pl-2"
              : "text-amber-800"}
          >
            <span className="font-semibold">{name(s)}</span> — {rowDetail(s)}
            {s.reason ? <div className="text-xs">{s.reason}</div> : null}
          </li>
        ))}
        {calm.map((s) => (
          <li key={s.sourceSystem} data-testid={`coverage-row-${s.state}`} className="text-content-secondary">
            <Clock className="w-3 h-3 inline mr-1 -mt-0.5" />
            <span className="font-semibold">{name(s)}</span> — {rowDetail(s)}
            {s.reason ? <div className="text-xs">{s.reason}</div> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Says whether the change feed is actually watching.
 *
 * Without this the tab is dishonest by omission: an unconsented feed and a genuinely quiet week
 * both render as no rows, and a reader would reasonably conclude nobody has touched the agent.
 * The retention window matters for the same reason — "nothing here" only ever means "nothing in
 * the last seven days".
 */
function BehaviorCoverageNote({ feed }) {
  if (!feed) {
    return (
      <div className="mt-3 pt-2 border-t border-border text-xs text-content-secondary">
        Change monitoring has not run yet. Entra permission and credential changes appear here after
        the next scan.
      </div>
    );
  }

  const healthy = feed.status === "Healthy";
  return (
    <div className={`mt-3 pt-2 border-t border-border text-xs ${
      healthy ? "text-content-secondary" : "text-amber-700"
    }`}>
      {healthy ? (
        <>
          Watching Entra for permission and credential changes
          {feed.retentionDays ? ` · ${feed.retentionDays}-day retention window` : ""}
          {feed.lastPolledAt ? ` · last checked ${timeAgo(feed.lastPolledAt)}` : ""}
        </>
      ) : (
        <>
          <AlertTriangle className="w-3 h-3 inline mr-1 -mt-0.5" />
          Change monitoring is not active, so this list may be incomplete.
          {feed.message ? ` ${feed.message}` : ""}
        </>
      )}
    </div>
  );
}

/**
 * A titled group in the settings screen. The screen had grown to thirteen controls in one flat
 * list, where a master switch, a provider connection and a notification preference all looked
 * alike — so the only way to find anything was to read every row.
 */
function SettingsSection({ title, description, children }) {
  return (
    <div className="mt-5">
      <div className="text-sm font-semibold text-content-primary">{title}</div>
      {description && (
        <p className="text-xs text-content-secondary mt-0.5 mb-1">{description}</p>
      )}
      <div className="divide-y divide-border">{children}</div>
    </div>
  );
}

/**
 * Per-organization detection thresholds and allowlists (§8.4).
 *
 * Only policy is here. Provider facts — Entra's 30-day sign-in retention, AWS's 400-day tracking
 * window — and engine internals are deliberately not editable, and the copy says so rather than
 * leaving their absence to look like an oversight.
 *
 * Warnings come from the server, which knows which combinations leave a detection unable to fire.
 * A settings screen that looks configured while the detection is inert is the failure this guards.
 */
function DetectionTuning({ config, setConfig, canManage }) {
  const settings = config.detectionSettings ?? {};
  const warnings = config.detectionWarnings ?? [];

  const setField = (key, value) =>
    setConfig((c) => ({
      ...c,
      detectionSettings: { ...(c.detectionSettings ?? {}), [key]: value },
    }));

  const setList = (key, text) =>
    setField(key, text.split(",").map((t) => t.trim()).filter(Boolean));

  // Cost fields are not integers, and two of them mean something specific when left blank, so they
  // cannot share the integer inputs above: parseInt would turn a 2.5x multiplier into 2, and would
  // turn an empty budget into 0 — a budget of nothing, which every month instantly exceeds.
  const setDecimal = (key, text) => {
    const trimmed = (text ?? "").trim();
    if (trimmed === "") return setField(key, null);
    const value = Number(trimmed);
    return setField(key, Number.isFinite(value) ? value : null);
  };

  const NUMBERS = [
    { key: "dormancyDays", label: "Dormant after (days)",
      help: "Silence before unexercised privilege is a finding." },
    { key: "minObservedDaysForDormancy", label: "Coverage required (days)",
      help: "Telemetry needed before dormancy may be claimed at all." },
    { key: "staleCredentialDays", label: "Stale credential after (days)",
      help: "Unused provider API keys." },
    { key: "authFailureThreshold", label: "Auth failures before reporting",
      help: "Refused authentications in the window. Both this and the rate must be met." },
    { key: "authFailureRatePercent", label: "Auth failure rate (%)",
      help: "Share of attempts that failed. Guards against busy agents with a few normal failures." },
    { key: "estateChangeNewsWindowDays", label: "Estate change window (days)",
      help: "How recently something must appear to count as news." },
    { key: "notificationLookbackHours", label: "Notification lookback (hours)",
      help: "Backlog guard when notifications are first enabled." },
    { key: "digestHourUtc", label: "Digest hour (UTC)",
      help: "When the daily estate summary sends." },
    { key: "deletedCommentRetentionDays", label: "Removed comment retention (days)",
      help: "Removed comments vanish immediately; their text is erased from the database after this many days (audit metadata is kept)." },
  ];

  return (
    <div className="py-3">
      <div>
          {warnings.length > 0 && (
            <div className="mb-3 p-2 rounded-lg bg-amber-50 border border-amber-200">
              {warnings.map((w) => (
                <div key={w} className="text-xs text-amber-800 flex items-start gap-1.5">
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                  <span>{w}</span>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {NUMBERS.map((n) => (
              <div key={n.key}>
                <label className="block text-xs font-medium text-content-primary mb-0.5">
                  {n.label}
                </label>
                <input
                  type="number"
                  disabled={!canManage}
                  value={settings[n.key] ?? ""}
                  onChange={(e) => setField(n.key, parseInt(e.target.value, 10) || 0)}
                  className="w-full text-sm border border-border rounded-lg px-2 py-1 bg-theme-primary text-content-primary disabled:opacity-60"
                />
                <p className="text-[11px] text-content-secondary mt-0.5">{n.help}</p>
              </div>
            ))}
          </div>

          {/* Cost detections (§11a). Separated because these govern money rather than posture, and
              because the budget is the one setting whose absence is meaningful — there is no
              default, and without a figure the budget alert correctly never fires. */}
          <div className="mt-4 pt-3 border-t border-border">
            <h5 className="text-xs font-semibold text-content-primary mb-2">Cost detections</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-content-primary mb-0.5">
                  Monthly AI budget (USD)
                </label>
                <input
                  type="number"
                  step="1"
                  min="0"
                  disabled={!canManage}
                  value={settings.monthlyBudgetUsd ?? ""}
                  onChange={(e) => setDecimal("monthlyBudgetUsd", e.target.value)}
                  placeholder="none set"
                  className="w-full text-sm border border-border rounded-lg px-2 py-1 bg-theme-primary text-content-primary disabled:opacity-60"
                />
                <p className="text-[11px] text-content-secondary mt-0.5">
                  Leave blank for no budget. There is deliberately no default — alerting against a
                  figure nobody chose is worse than not alerting at all.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-content-primary mb-0.5">
                  Budget warning at (%)
                </label>
                <input
                  type="number"
                  step="5"
                  min="10"
                  max="100"
                  disabled={!canManage}
                  value={settings.budgetWarningThreshold != null
                    ? Math.round(settings.budgetWarningThreshold * 100)
                    : ""}
                  onChange={(e) => setDecimal(
                    "budgetWarningThreshold",
                    e.target.value === "" ? "" : String(Number(e.target.value) / 100))}
                  className="w-full text-sm border border-border rounded-lg px-2 py-1 bg-theme-primary text-content-primary disabled:opacity-60"
                />
                <p className="text-[11px] text-content-secondary mt-0.5">
                  First warning, as a share of the month's budget. Exceeding it announces separately.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-content-primary mb-0.5">
                  Spend spike multiple
                </label>
                <input
                  type="number"
                  step="0.5"
                  min="1.5"
                  disabled={!canManage}
                  value={settings.spendSpikeMultiplier ?? ""}
                  onChange={(e) => setDecimal("spendSpikeMultiplier", e.target.value)}
                  className="w-full text-sm border border-border rounded-lg px-2 py-1 bg-theme-primary text-content-primary disabled:opacity-60"
                />
                <p className="text-[11px] text-content-secondary mt-0.5">
                  How far above its own trailing median a day must be. A multiple rather than a
                  figure, since any fixed amount is noise for one estate and silence for another.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-content-primary mb-0.5">
                  Usage baseline required (days)
                </label>
                <input
                  type="number"
                  step="1"
                  min="3"
                  disabled={!canManage}
                  value={settings.usageBaselineDays ?? ""}
                  onChange={(e) => setField("usageBaselineDays", parseInt(e.target.value, 10) || 0)}
                  className="w-full text-sm border border-border rounded-lg px-2 py-1 bg-theme-primary text-content-primary disabled:opacity-60"
                />
                <p className="text-[11px] text-content-secondary mt-0.5">
                  Recorded days before spikes and new-model alerts may fire at all. Without a
                  before, everything is new and every day is a spike.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-content-primary mb-0.5">
                  Copilot seat price (per month)
                </label>
                <input
                  type="number"
                  step="1"
                  min="0"
                  disabled={!canManage}
                  value={settings.copilotSeatCostPerMonth ?? ""}
                  onChange={(e) => setDecimal("copilotSeatCostPerMonth", e.target.value)}
                  placeholder="not set"
                  className="w-full text-sm border border-border rounded-lg px-2 py-1 bg-theme-primary text-content-primary disabled:opacity-60"
                />
                <p className="text-[11px] text-content-secondary mt-0.5">
                  What you actually pay per seat. No provider exposes a negotiated price, so seat
                  cost is only ever shown when you enter it here — and is labelled as declared.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-content-primary mb-0.5">
                  Unowned spend is a finding above (USD)
                </label>
                <input
                  type="number"
                  step="10"
                  min="0"
                  disabled={!canManage}
                  value={settings.unownedSpendFindingThresholdUsd ?? ""}
                  onChange={(e) => setDecimal("unownedSpendFindingThresholdUsd", e.target.value)}
                  className="w-full text-sm border border-border rounded-lg px-2 py-1 bg-theme-primary text-content-primary disabled:opacity-60"
                />
                <p className="text-[11px] text-content-secondary mt-0.5">
                  30-day spend above which an asset with no accountable owner becomes a finding.
                  Keeps trivial consumption out of an accountability report.
                </p>
              </div>
            </div>
          </div>

          {/* Behavior drift (§11e.2). One finding per asset comparing its recent behavior against
              its own baseline — the windows and floors here bound when that comparison may speak.
              A change that persists for a baseline window becomes the new baseline, so drift
              findings clear themselves; these settings tune sensitivity, not lifecycle. */}
          <div className="mt-4 pt-3 border-t border-border">
            <h5 className="text-xs font-semibold text-content-primary mb-2">Behavior drift</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-content-primary mb-0.5">
                  Baseline window (days)
                </label>
                <input
                  type="number"
                  step="1"
                  min="7"
                  max="90"
                  disabled={!canManage}
                  value={settings.driftBaselineDays ?? ""}
                  onChange={(e) => setField("driftBaselineDays", parseInt(e.target.value, 10) || 0)}
                  className="w-full text-sm border border-border rounded-lg px-2 py-1 bg-theme-primary text-content-primary disabled:opacity-60"
                />
                <p className="text-[11px] text-content-secondary mt-0.5">
                  Days of the asset's own history that define normal. With the recent window, must
                  fit inside Entra's 30-day sign-in retention or the authentication dimension
                  cannot fire.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-content-primary mb-0.5">
                  Recent window (days)
                </label>
                <input
                  type="number"
                  step="1"
                  min="1"
                  max="28"
                  disabled={!canManage}
                  value={settings.driftRecentDays ?? ""}
                  onChange={(e) => setField("driftRecentDays", parseInt(e.target.value, 10) || 0)}
                  className="w-full text-sm border border-border rounded-lg px-2 py-1 bg-theme-primary text-content-primary disabled:opacity-60"
                />
                <p className="text-[11px] text-content-secondary mt-0.5">
                  The behavior being judged. Should be several times shorter than the baseline.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-content-primary mb-0.5">
                  Drift multiple
                </label>
                <input
                  type="number"
                  step="0.5"
                  min="1.5"
                  disabled={!canManage}
                  value={settings.driftRateMultiple ?? ""}
                  onChange={(e) => setDecimal("driftRateMultiple", e.target.value)}
                  className="w-full text-sm border border-border rounded-lg px-2 py-1 bg-theme-primary text-content-primary disabled:opacity-60"
                />
                <p className="text-[11px] text-content-secondary mt-0.5">
                  How many times above or below its own baseline rate a dimension must move.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-content-primary mb-0.5">
                  Ignore below (sign-ins/day)
                </label>
                <input
                  type="number"
                  step="1"
                  min="1"
                  disabled={!canManage}
                  value={settings.driftMinDailyCount ?? ""}
                  onChange={(e) => setField("driftMinDailyCount", parseInt(e.target.value, 10) || 0)}
                  className="w-full text-sm border border-border rounded-lg px-2 py-1 bg-theme-primary text-content-primary disabled:opacity-60"
                />
                <p className="text-[11px] text-content-secondary mt-0.5">
                  Floor for the authentication dimension — two sign-ins against one is a tripled
                  rate and means nothing.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-content-primary mb-0.5">
                  Ignore below (USD/day)
                </label>
                <input
                  type="number"
                  step="0.5"
                  min="0.01"
                  disabled={!canManage}
                  value={settings.driftMinDailySpendUsd ?? ""}
                  onChange={(e) => setDecimal("driftMinDailySpendUsd", e.target.value)}
                  className="w-full text-sm border border-border rounded-lg px-2 py-1 bg-theme-primary text-content-primary disabled:opacity-60"
                />
                <p className="text-[11px] text-content-secondary mt-0.5">
                  Floor for the spend dimension, so cents-level estates never fire.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-content-primary mb-0.5">
                  New resources before churn drifts
                </label>
                <input
                  type="number"
                  step="1"
                  min="2"
                  disabled={!canManage}
                  value={settings.driftResourceChurnCount ?? ""}
                  onChange={(e) => setField("driftResourceChurnCount", parseInt(e.target.value, 10) || 0)}
                  className="w-full text-sm border border-border rounded-lg px-2 py-1 bg-theme-primary text-content-primary disabled:opacity-60"
                />
                <p className="text-[11px] text-content-secondary mt-0.5">
                  First-use announces each new resource individually; drift speaks to the rate of
                  them inside the recent window.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-content-primary mb-0.5">
                Allowed tools
              </label>
              <input
                type="text"
                disabled={!canManage}
                defaultValue={(settings.allowedTools ?? []).join(", ")}
                onBlur={(e) => setList("allowedTools", e.target.value)}
                placeholder="ollama, internal-rag"
                className="w-full text-sm border border-border rounded-lg px-2 py-1 bg-theme-primary text-content-primary disabled:opacity-60"
              />
              <p className="text-[11px] text-content-secondary mt-0.5">
                Never raise findings, and never appear in endpoint review. Whole names or node
                keys, comma separated.
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-content-primary mb-0.5">
                Assets with expected activity
              </label>
              <input
                type="text"
                disabled={!canManage}
                defaultValue={(settings.allowedAssets ?? []).join(", ")}
                onBlur={(e) => setList("allowedAssets", e.target.value)}
                placeholder="nightly-batch-agent"
                className="w-full text-sm border border-border rounded-lg px-2 py-1 bg-theme-primary text-content-primary disabled:opacity-60"
              />
              <p className="text-[11px] text-content-secondary mt-0.5">
                Exempt from dormancy and unused-permission findings only — a batch agent that
                legitimately runs rarely. Ownership, privilege and exposure findings still apply;
                to set one of those aside, mark it not a risk on the finding itself. Names or
                asset ids.
              </p>
            </div>
          </div>

          <p className="text-[11px] text-content-secondary mt-3">
            To silence one specific finding with a recorded reason, use “Record as not a risk” on the
            asset instead — that decision is accountable and lapses when the evidence changes.
            Provider limits (Entra retains 30 days of sign-ins, AWS reports 400, Google Cloud token history about 41 days) and internal polling
            behaviour are not adjustable.
          </p>
      </div>
    </div>
  );
}

// Module-scope (NOT nested in SettingsTab): a component type defined inside another component is
// re-created on every parent render, which remounts its DOM and drops text-input focus per keystroke.
function AwsConnectorPicker({ config, setConfig, awsConnectors, canManage }) {
  // Local text state so typing separators ("us-east-1, ") isn't eaten by the parse→join round-trip;
  // the parsed array is pushed into config on every change.
  const [regionsText, setRegionsText] = useState((config.awsDiscoveryRegions || []).join(", "));

  const selectedAwsIds = config.awsConnectorIds; // null = all active connectors
  const isAwsSelected = (id) => selectedAwsIds == null || selectedAwsIds.includes(id);
  const toggleAwsConnector = (id) => {
    const current = selectedAwsIds == null ? awsConnectors.map((c) => c.id) : selectedAwsIds;
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    setConfig({ ...config, awsConnectorIds: next });
  };

  if (awsConnectors.length === 0) {
    return (
      <div className="ml-7 mb-2 text-xs text-content-secondary">
        No AWS connectors configured — add one under Settings → Integrations → GRC Connectors (AWS).
      </div>
    );
  }
  return (
    <div className="ml-7 mb-2 space-y-1">
      {awsConnectors.map((c) => (
        <label key={c.id} className={`flex items-center gap-2 text-xs ${canManage ? "cursor-pointer" : "opacity-60"}`}>
          <input
            type="checkbox"
            checked={isAwsSelected(c.id)}
            disabled={!canManage}
            onChange={() => toggleAwsConnector(c.id)}
          />
          <span className="text-content-primary">{c.displayName || `AWS connector ${c.id}`}</span>
          {c.isActive === false && <span className="text-content-secondary">(inactive — skipped)</span>}
        </label>
      ))}
      <div className="flex items-center gap-2 mt-1.5">
        <span className="text-xs text-content-primary">Regions</span>
        <input
          type="text"
          placeholder="connector's region (default)"
          value={regionsText}
          disabled={!canManage}
          onChange={(e) => {
            setRegionsText(e.target.value);
            const regions = e.target.value.split(/[\s,]+/).filter((r) => r.length > 0);
            setConfig({ ...config, awsDiscoveryRegions: regions.length > 0 ? regions : null });
          }}
          className="flex-1 border border-border rounded-md px-2 py-1 text-xs bg-theme-secondary text-content-primary"
        />
      </div>
      <div className="text-[11px] text-content-secondary">
        Comma-separated AWS regions to scan (e.g. us-east-1, us-west-2). Empty = each connector's own region.
      </div>
    </div>
  );
}

// Generic GRC-connector picker for AI provider sources (OpenAI/Anthropic) — module scope for the
// same remount reason as AwsConnectorPicker. idsField: config field holding the id list (null = all).
function ProviderConnectorPicker({ config, setConfig, connectors, canManage, idsField, providerLabel }) {
  const selected = config[idsField]; // null | number[]
  const isSelected = (id) => selected == null || selected.includes(id);
  const toggleId = (id) => {
    const current = selected == null ? connectors.map((c) => c.id) : selected;
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    setConfig({ ...config, [idsField]: next });
  };
  if (connectors.length === 0) {
    return (
      <div className="ml-7 mb-2 text-xs text-content-secondary">
        No {providerLabel} connectors configured — add one under Settings → Integrations → GRC Connectors ({providerLabel}).
      </div>
    );
  }
  return (
    <div className="ml-7 mb-2 space-y-1">
      {connectors.map((c) => (
        <label key={c.id} className={`flex items-center gap-2 text-xs ${canManage ? "cursor-pointer" : "opacity-60"}`}>
          <input type="checkbox" checked={isSelected(c.id)} disabled={!canManage} onChange={() => toggleId(c.id)} />
          <span className="text-content-primary">{c.displayName || `${providerLabel} connector ${c.id}`}</span>
          {c.isActive === false && <span className="text-content-secondary">(inactive — skipped)</span>}
        </label>
      ))}
    </div>
  );
}

/** A declared date (yyyy-MM-dd) as a calendar date, read in UTC so it never shifts a day. */
export function formatDeclaredDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
}

/**
 * Whether the Anthropic activity feed can read anything: the connectors a cycle would read are
 * the active Anthropic ones the config selects (null = all), and at least one of them must hold a
 * compliance key, its key id and a declared enablement date. The key's presence is read from the
 * connector's non-secret `anthropicComplianceKeyConfigured` flag only.
 */
export function anthropicActivityReadiness(config, grcConnectors = []) {
  const selectedIds = config?.anthropicConnectorIds;
  const connectors = (grcConnectors || [])
    .filter((c) => c.sourceSystem === "ANTHROPIC" && c.isActive !== false)
    .filter((c) => selectedIds == null || selectedIds.includes(c.id));
  const missingFor = (c) => [
    c.anthropicComplianceKeyConfigured === true ? null : "a compliance key",
    c.anthropicComplianceKeyId ? null : "the compliance key's id",
    c.anthropicComplianceEnabledAt ? null : "the date the Compliance API was enabled",
  ].filter(Boolean);
  const ready = connectors.some((c) => missingFor(c).length === 0);
  let reason = null;
  if (connectors.length === 0) {
    reason = "Unavailable: no active Anthropic connector is selected. Add one under Settings → Integrations.";
  } else if (!ready) {
    reason = `Unavailable until an Anthropic connector has a compliance key, its key id and the date the Compliance API was enabled. ${
      connectors.map((c) => `${c.displayName || `Anthropic connector ${c.id}`} is missing ${missingFor(c).join(", ")}.`).join(" ")}`;
  }
  return { ready, reason, connectors };
}

function SettingsTab({
  config, setConfig, connections = [], awsConnectors = [], grcConnectors = [],
  canManage, canAdminister, saving, onSave, onSavePolicy, savingPolicy, policyNotice,
  dirty, onClose,
}) {
  // Runtime actions and AI coding agents on managed devices are preview sources: hidden while the
  // AI Intelligence preview flag is off. Their stored values are untouched and round-trip on save.
  const aiIntelPreview = useAiIntelPreview();
  if (!config) {
    return (
      <Card className="p-6 text-sm text-content-secondary">Loading discovery configuration…</Card>
    );
  }

  const toggle = (key) => setConfig({ ...config, [key]: !config[key] });
  const anthropicActivity = anthropicActivityReadiness(config, grcConnectors);

  // Connection selection: null = all connections (default). The list only becomes explicit
  // once a user unchecks something — from then on, new connections must be selected here.
  const selectedIds = config.aiDiscoveryConnectionIds; // null | number[]
  const isSelected = (id) => selectedIds == null || selectedIds.includes(id);
  const toggleConnection = (id) => {
    const current = selectedIds == null ? connections.map((c) => c.id) : selectedIds;
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    setConfig({ ...config, aiDiscoveryConnectionIds: next });
  };

  const ConnectionPicker = ({ provider }) => {
    const providerConnections = connections.filter((c) => c.provider === provider);
    if (providerConnections.length === 0) {
      return (
        <div className="ml-7 mb-2 text-xs text-content-secondary">
          No {provider} connections configured — add one under Settings → Integrations → Source Code Connections.
        </div>
      );
    }
    return (
      <div className="ml-7 mb-2 space-y-1">
        {providerConnections.map((c) => (
          <label key={c.id} className={`flex items-center gap-2 text-xs ${canManage ? "cursor-pointer" : "opacity-60"}`}>
            <input
              type="checkbox"
              checked={isSelected(c.id)}
              disabled={!canManage}
              onChange={() => toggleConnection(c.id)}
            />
            <span className="text-content-primary">{c.displayName || `${c.provider} / ${c.target}`}</span>
            <span className="text-content-secondary">({c.target}{c.baseUrl ? ` · ${c.baseUrl}` : ""})</span>
          </label>
        ))}
        {selectedIds != null && (
          <div className="text-[11px] text-content-secondary">
            Selection is explicit — connections added later must be checked here before they are scanned.
          </div>
        )}
      </div>
    );
  };
  const Toggle = ({ id, label, description, disabled, badge, onChange }) => (
    <label className={`flex items-start gap-3 py-3 ${disabled ? "opacity-60" : "cursor-pointer"}`}>
      <input
        type="checkbox"
        checked={!!config[id]}
        disabled={disabled || !canManage}
        onChange={onChange || (() => toggle(id))}
        className="mt-1"
      />
      <div>
        <div className="text-sm font-medium text-content-primary flex items-center gap-2">
          {label}
          {badge && <span className="px-1.5 py-0.5 rounded text-[10px] bg-gray-100 text-gray-600 border border-gray-300">{badge}</span>}
        </div>
        <div className="text-xs text-content-secondary mt-0.5">{description}</div>
      </div>
    </label>
  );

  return (
    <Card className="p-6">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h2 className="text-lg font-semibold text-content-primary">Discovery settings</h2>
        <div className="flex items-center gap-2">
          {dirty && (
            <span className="px-2 py-0.5 rounded text-[11px] font-medium bg-yellow-50 text-yellow-800 border border-yellow-200">
              Unsaved changes
            </span>
          )}
          <button
            onClick={onClose}
            className="p-1.5 rounded-md border border-border hover:bg-theme-hover"
            title="Close discovery settings"
            aria-label="Close discovery settings"
          >
            <X className="w-4 h-4 text-content-secondary" />
          </button>
        </div>
      </div>
      <p className="text-xs text-content-secondary mb-4">
        Discovery reads your existing connections (Settings → Integrations): the Microsoft connection
        for Entra, Azure and Defender, and Source Code Connections for GitHub. Microsoft Defender for
        Endpoint supplies the device inventory. Axaris installs no software and runs no scanner of its
        own; it reads what your existing services already report.
      </p>

      {/* Master switch first and alone: it gates everything below, so grouping it with the
          per-source toggles invited people to read it as just another source. */}
      <div className="pb-3 border-b border-border">
        <Toggle
          id="isEnabled"
          label="AI asset discovery"
          description="Master switch for the discovery sweep. Everything below is inactive while this is off."
        />
        <div className="flex items-start gap-3 mt-1 ml-7">
          <label className="text-xs text-content-secondary mt-1.5">Scan frequency</label>
          <AiScanFrequencySelect
            valueMinutes={config.scanFrequencyMinutes ?? 1440}
            onChange={(m) => setConfig({ ...config, scanFrequencyMinutes: m })}
            disabled={!canManage}
          />
        </div>
      </div>

      {/* Two columns above lg: the flat single column had grown past two screens of scrolling.
          Sources and permissions on the left; tuning and feed operations on the right. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 items-start">
      <div>
      <SettingsSection
        title="Discovery sources"
        description="Where Axaris looks for AI. Each uses a connection you have already configured under Settings → Integrations."
      >
        <div>
          <Toggle
            id="anthropicDiscoveryEnabled"
            label="Anthropic organization"
            description="Workspaces and API keys via the Admin API. Organization accounts only — unavailable on individual Console accounts."
          />
          {config.anthropicDiscoveryEnabled && (
            <ProviderConnectorPicker
              config={config}
              setConfig={setConfig}
              connectors={grcConnectors.filter((c) => c.sourceSystem === "ANTHROPIC")}
              canManage={canManage}
              idsField="anthropicConnectorIds"
              providerLabel="Anthropic"
            />
          )}
        </div>
        <div>
          <Toggle
            id="openAiDiscoveryEnabled"
            label="OpenAI organization"
            description="Projects, API keys and their last use, via the Admin API. Requires an OpenAI admin key connector."
          />
          {config.openAiDiscoveryEnabled && (
            <ProviderConnectorPicker
              config={config}
              setConfig={setConfig}
              connectors={grcConnectors.filter((c) => c.sourceSystem === "OPENAI")}
              canManage={canManage}
              idsField="openAiConnectorIds"
              providerLabel="OpenAI"
            />
          )}
        </div>
        <Toggle
          id="copilotStudioDiscoveryEnabled"
          label="Copilot Studio"
          description="Maker-built agents across your Power Platform environments — the estate that never registers in Entra. The Axaris app must also be registered as a Power Platform admin management application (the connection diagnostic shows the command)."
        />
        {aiIntelPreview && (
          <Toggle
            id="localAgentDiscoveryEnabled"
            label="AI coding agents on managed devices"
            description="Finds Cursor, Codex CLI and similar AI agents installed on devices through your existing Microsoft Defender connection. Requires Microsoft Defender for Endpoint on those devices. Agents found are listed as shadow AI by default."
          />
        )}
        <Toggle
          id="azureDiscoveryEnabled"
          label="Azure AI resources"
          description="Azure OpenAI, AI Search and model deployments via Resource Graph."
        />
        <Toggle
          id="entraDiscoveryEnabled"
          label="Microsoft Entra agent identities"
          description="Agent identities and AI service principals, with the permissions they hold."
        />
        <div>
          <Toggle
            id="awsDiscoveryEnabled"
            label="AWS Bedrock and SageMaker"
            description="Bedrock agents with their action groups and knowledge bases, plus SageMaker endpoints. Read-only; see the setup guide for the IAM policy."
          />
          {config.awsDiscoveryEnabled && (
            <AwsConnectorPicker
              config={config}
              setConfig={setConfig}
              awsConnectors={awsConnectors}
              canManage={canManage}
            />
          )}
        </div>
        <div>
          {/* No connector or location picker: an organization has at most one Google Cloud
              connector, and discovery learns locations from what the inventory returns. */}
          <Toggle
            id="gcpDiscoveryEnabled"
            label="Google Cloud (Vertex AI)"
            description="Vertex AI agents (reasoning engines) and endpoints, with the service accounts they run as and the models they use, via Cloud Asset Inventory. Read-only; see the setup guide for the roles. Google's asset inventory can lag the live estate by several hours, so a very recently created Vertex AI resource may not appear yet. Google Cloud spend is not shown, because Axaris cannot read it; that is different from an agent costing nothing."
          />
          {config.gcpDiscoveryEnabled && (
            <div className="ml-7 mb-2 space-y-1">
              {!grcConnectors.some((c) => c.sourceSystem === "GCP") && (
                <div className="text-xs text-content-secondary">
                  No Google Cloud connector configured — add one under Settings → Integrations → GRC Connectors (Google Cloud).
                </div>
              )}
              <div className="text-[11px] text-content-secondary">
                Vertex AI token usage is reported per Google Cloud project, not per agent or endpoint
                (Google's metric names neither), and its input-token figure may include cached tokens
                because Google does not report the split. Google keeps about six weeks of token
                history, so Vertex AI usage goes back about 41 days, not the 90 days other providers
                backfill.
              </div>
            </div>
          )}
        </div>
        {aiIntelPreview && (
          <div>
            {/* Never disabled by its notes: the customer sees what the setting is and what it still
                needs. Both flags are derived server-side from the Microsoft connection — the same two
                conditions the feed runner checks before it reads anything. */}
            <Toggle
              id="runtimeActionFeedEnabled"
              label="Runtime actions"
              description="Reads which Microsoft Graph calls your agents actually make."
            />
            {(config.graphActivityWorkspaceConfigured !== true || config.graphActivityConsentGranted !== true) && (
              <div className="ml-7 mb-2 space-y-1">
                {config.graphActivityWorkspaceConfigured !== true && (
                  <div data-testid="runtime-actions-workspace-note" className="text-xs text-content-secondary">
                    Add a Log Analytics workspace ID on the Microsoft connection before this can read anything.
                  </div>
                )}
                {config.graphActivityConsentGranted !== true && (
                  <div data-testid="runtime-actions-consent-note" className="text-xs text-content-secondary">
                    Turn on AI discovery on the Microsoft connection before this can read anything.
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        <div data-testid="anthropic-activity-setting">
          {/* Disabled (while off) until a connector the feed would read holds a compliance key,
              its key id and a declared date: without all three every cycle would only skip. The
              reason is shown, never a silent no-op. Turning it off is always allowed. The key's
              presence comes from the connector's non-secret configured flag; the key never
              reaches the browser. */}
          <Toggle
            id="anthropicActivityFeedEnabled"
            label="Anthropic activity"
            description="Reads the Anthropic Compliance API activity feed and records the activity it observes: who acted, on what and when, never prompt, response or tool text. Off by default."
            disabled={!anthropicActivity.ready && !config.anthropicActivityFeedEnabled}
          />
          <div className="ml-7 mb-2 space-y-1">
            {!anthropicActivity.ready && (
              <div data-testid="anthropic-activity-disabled-reason" className="text-xs text-amber-700">
                {anthropicActivity.reason}
              </div>
            )}
            <div data-testid="anthropic-activity-key-note" className="text-xs text-content-secondary">
              This feed needs its own Anthropic admin key, minted after the Compliance API was enabled
              for your Anthropic organization. An existing admin key will not work. Supply the key's id
              as well, so Axaris can remove its own polling from what it records. Both are set on the
              Anthropic connector under Settings → Integrations.
            </div>
            {anthropicActivity.connectors.map((c) => (
              <div key={c.id} data-testid={`anthropic-activity-declared-${c.id}`} className="text-xs text-content-secondary">
                <span className="text-content-primary">
                  {c.displayName || `Anthropic connector ${c.id}`}
                </span>
                {" · "}Compliance API enabled on (declared):{" "}
                <span className="text-content-primary">
                  {c.anthropicComplianceEnabledAt ? formatDeclaredDate(c.anthropicComplianceEnabledAt) : "not declared"}
                </span>
              </div>
            ))}
            {anthropicActivity.connectors.length > 0 && (
              <div className="text-[11px] text-content-secondary">
                Anthropic records nothing before this date and performs no backfill, so activity before
                it is not missing: it was never recorded. The date is edited on the Anthropic connector.
              </div>
            )}
          </div>
        </div>
        <div>
          <Toggle
            id="gitHubDiscoveryEnabled"
            label="GitHub"
            description="AI framework use, SDK calls and MCP server configurations in your repositories."
          />
          {config.gitHubDiscoveryEnabled && <ConnectionPicker provider="GitHub" />}
        </div>
        <div>
          <Toggle
            id="gitLabDiscoveryEnabled"
            label="GitLab"
            description="AI framework use, SDK calls and MCP server configurations in your projects."
          />
          {config.gitLabDiscoveryEnabled && <ConnectionPicker provider="GitLab" />}
        </div>
        <div>
          <Toggle
            id="azureDevOpsDiscoveryEnabled"
            label="Azure DevOps"
            description="AI framework use, SDK calls and MCP server configurations in your repositories."
          />
          {config.azureDevOpsDiscoveryEnabled && <ConnectionPicker provider="AzureDevOps" />}
        </div>
      </SettingsSection>

      <SettingsSection
        title="Response and alerts"
        description="What Axaris is allowed to do on its own, and who hears about it."
      >
        <Toggle
          id="responseEnabled"
          label="Enable response actions"
          description="Lets risk managers request containment of discovered AI assets — disabling Anthropic workspace keys, deleting OpenAI project keys, quarantining AWS agent roles, disabling the service account a Google Cloud agent runs as, or disabling an Entra identity. Off by default. Turning it off turns every action off, including Disable Entra identity, which also needs response consent on the Microsoft connection. Pending requests can still be rejected, and past actions can still be reversed."
        />
        <Toggle
          id="responseOverrideEnabled"
          label="Allow immediate response actions"
          description="Lets an operator execute a containment without routing it to an approver. A reason is always required, the action is marked as executed without approval, and the organization's other risk managers are notified. Turn this off to require an approver every time."
        />
        {(config.responseOverrideEnabled || config.tier1ImmediateAllowed || config.irreversibleImmediateAllowed) && (
          <div className="ml-7 border-l-2 border-border pl-3">
            {/* §11i response policy: default off, ADMIN+ only, saved immediately through its own
                endpoint (never part of "Save settings"), and every change is announced in
                "What changed" — a loosened rail must not be quiet. */}
            <Toggle
              id="tier1ImmediateAllowed"
              label="Tier 1 assets may execute immediately"
              description="By default, Tier 1 (most critical) assets require a second person's approval. Turning this on lets a single risk manager contain a Tier 1 asset immediately."
              disabled={!canAdminister || savingPolicy}
              onChange={() => onSavePolicy({ tier1ImmediateAllowed: !config.tier1ImmediateAllowed })}
            />
            <Toggle
              id="irreversibleImmediateAllowed"
              label="Irreversible actions may execute immediately"
              description="By default, permanent actions (deleting OpenAI project keys — there is no restore) require a second person's approval. Turning this on lets a single person destroy provider keys with no review."
              disabled={!canAdminister || savingPolicy}
              onChange={() => onSavePolicy({ irreversibleImmediateAllowed: !config.irreversibleImmediateAllowed })}
            />
            {policyNotice && (
              <div className="text-[11px] text-amber-700 pb-2">{policyNotice}</div>
            )}
            {!config.responseOverrideEnabled && (
              <div className="text-[11px] text-amber-700 pb-2">
                Immediate response actions are off, so these policies are currently inactive —
                they apply again automatically if immediate actions are re-enabled.
              </div>
            )}
            <div className="text-[11px] text-content-secondary pb-2">
              {canAdminister
                ? "These two policies save immediately and are announced in \"What changed\"."
                : "Only an organization administrator can change these two policies. Changes are announced in \"What changed\"."}
            </div>
          </div>
        )}
        <Toggle
          id="estateChangeNotificationsEnabled"
          label="Email estate changes"
          description="Sends a daily summary of what moved in your AI estate to the organization's risk managers and each asset's owner. Two changes do not wait for the summary: a credential added to an agent identity, and a containment that was undone outside Axaris."
        />
      </SettingsSection>

      <SettingsSection
        title="Tier guidance"
        description="What each criticality tier means in your organization — shown on the Tiers tab and wherever tiers are set. Blank uses the built-in description. Guidance is content only; the response rules a tier carries are governed above."
      >
        {[
          ["1", "Tier 1 — Most critical"],
          ["2", "Tier 2 — Standard"],
          ["3", "Tier 3 — Sandbox / experimental"],
        ].map(([key, label]) => (
          <div key={key} className="py-2">
            <label className="block text-xs font-medium text-content-primary mb-0.5">{label}</label>
            <textarea
              rows={2}
              maxLength={500}
              disabled={!canManage}
              value={config.tierGuidance?.[key] || ""}
              onChange={(e) => setConfig({
                ...config,
                tierGuidance: { ...(config.tierGuidance || {}), [key]: e.target.value },
              })}
              placeholder="Uses the built-in description when blank."
              className="w-full text-xs border border-border rounded-lg px-2 py-1.5 bg-theme-primary text-content-primary disabled:opacity-60"
            />
          </div>
        ))}
      </SettingsSection>
      </div>

      <div>
      <SettingsSection
        title="Detection tuning"
        description="Thresholds and allowlists for this organization. Defaults suit most estates."
      >
        <DetectionTuning config={config} setConfig={setConfig} canManage={canManage} />
      </SettingsSection>

      <SettingsSection
        title="Telemetry feeds"
        description="The feeds that run on their own schedule rather than with a scan — what each one last did, and whether it worked."
      >
        <AiFeedOperations canManage={canManage} grcConnectors={grcConnectors} />
      </SettingsSection>
      </div>
      </div>

      <div className="flex items-center justify-between mt-6">
        <span className="text-xs text-content-secondary">
          {config.lastScannedAt ? `Last scanned ${timeAgo(config.lastScannedAt)}` : "Never scanned"}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 rounded-md border border-border text-sm text-content-secondary hover:bg-theme-hover"
          >
            {canManage ? "Cancel" : "Close"}
          </button>
          {canManage && (
            <Button onClick={onSave} disabled={saving}>
              {saving ? "Saving…" : "Save settings"}
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
